// BravoDashboard.js — v3.1 (2026-04-14)
// FIX #1 — Night Shift: separate API POST with shift='NIGHT' and correct hon_count.
//           fetchEntries maps night_hon_qty → nightHon, night_hon_count → nightHonCount.
// FIX #2 — H.ON Count: mapped from API response (day_hon_count / night_hon_count).
//           Requires bravo.php getSummary() to include day/night hon_count in SQL (see bravo.php fix).
// FIX #3 — Column order: Total H.ON Count column appears BEFORE Total H.ON in
//           table header, data rows, and totals footer row.
// BravoDashboard.js  — v4.0 (2026-04-14)
// Changes vs v3:
//  FIX #1 (CRITICAL) — Night shift data now saves correctly:
//                       handleAddSave makes two separate POST calls (DAY + NIGHT)
//                       so each shift is stored as its own row in ppc_bravo_entries.
//  FIX #2 — H.ON Count now reflects properly in table:
//            DAY call sends dayHonCount, NIGHT call sends nightHonCount.
//            Optimistic state update also persists both counts immediately.
//  FIX #3 — Total H.ON Count column now appears BEFORE Total H.ON in:
//            table header, data rows, and tfoot totals row.
//  All other logic (Incentive tab, Report tab, Admin Panel, Edit Requests)
//  is unchanged.

import { useState, useEffect, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { PPC_API_URL, usePPCAuth } from "./PPCContext";

// ── Constants ─────────────────────────────────────────────────────────────────
const DEHD_FACTOR = 0.68;   // Deheading = H.ON Qty × 0.68
const INC_STD     = 60;     // Standard tokens per associate

const CONTRACTORS = [
  "DBS","KRISHNA","MALE ASSOCIATES","SAMUDRA","SHIVA",
  "SHUKOOR ISM","SHUKOOR LOCAL","SONA CIST","SWF","VENKATESWARA",
];

const GRAD = "linear-gradient(135deg,#d32f2f 0%,#7c3aed 55%,#1565c0 100%)";

// ── Helpers ───────────────────────────────────────────────────────────────────
const deh    = (hon)  => Math.round(parseFloat(hon||0) * DEHD_FACTOR * 100) / 100;
const fmt2   = (n)    => parseFloat(n||0).toFixed(2);
const fmtK   = (n)    => parseInt(n||0).toLocaleString();
const now8   = ()     => new Date().toLocaleString("en-IN");

// ── Sample employee data (replace with API when backend ready) ────────────────
const EMPS_STATIC = [
  { name:"Venkat Rao",  id:"EMP-045", contractor:"DBS",          tokens:80 },
  { name:"Ravi Kumar",  id:"EMP-041", contractor:"DBS",          tokens:78 },
  { name:"Suresh Babu", id:"EMP-042", contractor:"DBS",          tokens:72 },
  { name:"Arjun Reddy", id:"EMP-043", contractor:"DBS",          tokens:65 },
  { name:"Mohan Das",   id:"EMP-044", contractor:"DBS",          tokens:55 },
  { name:"Nagarjuna T", id:"EMP-101", contractor:"KRISHNA",      tokens:75 },
  { name:"Balaji S",    id:"EMP-102", contractor:"KRISHNA",      tokens:68 },
  { name:"Prakash N",   id:"EMP-103", contractor:"KRISHNA",      tokens:62 },
  { name:"Siva Kumar",  id:"EMP-104", contractor:"KRISHNA",      tokens:57 },
  { name:"Rajesh M",    id:"EMP-201", contractor:"SHIVA",        tokens:63 },
  { name:"Srikanth R",  id:"EMP-301", contractor:"SONA CIST",    tokens:82 },
  { name:"Anand Kumar", id:"EMP-302", contractor:"SONA CIST",    tokens:77 },
  { name:"Satish G",    id:"EMP-401", contractor:"VENKATESWARA", tokens:59 },
];

// ── Component ─────────────────────────────────────────────────────────────────
export default function BravoDashboard() {
  const navigate  = useNavigate();
  const location  = useLocation();
  const auth      = usePPCAuth();

  const today = new Date().toISOString().slice(0, 10);

  // ── Core state ────────────────────────────────────────────────────────────
  const [selDate,      setSelDate]      = useState(today);
  const [activeTab,    setActiveTab]    = useState("bravo"); // bravo | inc | rpt | admin
  const [period,       setPeriod]       = useState("daily");
  const [target,       setTarget]       = useState(0);
  const [targetInp,    setTargetInp]    = useState("");
  const [loading,      setLoading]      = useState(false);
  const [toast,        setToast]        = useState(null);

  // ── Bravo data (live, replaces static DATA object) ────────────────────────
  const [entries,      setEntries]      = useState([]);  // raw rows from API / local
  const [isAdmin,      setIsAdmin]      = useState(false);

  // ── Edit request state ────────────────────────────────────────────────────
  const [editRequests, setEditRequests] = useState([]);
  const [editTarget,   setEditTarget]   = useState(null); // entry being requested

  // ── Add Entry modal ───────────────────────────────────────────────────────
  const [addOpen,      setAddOpen]      = useState(false);
  const [addCtor,      setAddCtor]      = useState("");
  const [addForm,      setAddForm]      = useState(blankAddForm());

  // ── Edit Request modal ────────────────────────────────────────────────────
  const [reqOpen,      setReqOpen]      = useState(false);
  const [reqForm,      setReqForm]      = useState(blankReqForm());

  // ── Incentive tab ─────────────────────────────────────────────────────────
  const [incFilter,     setIncFilter]    = useState("");
  const [incSelCtor,    setIncSelCtor]   = useState(null);
  const [incShowEmps,   setIncShowEmps]  = useState(true);
  // Employee details: ONLY records explicitly submitted via form — starts empty
  // Shape: [{ name, id, contractor, tokens, date, shift, submittedAt }]
  const [empDetailsData, setEmpDetailsData] = useState([]);
  // ADDED: state for inline edit + delete in incentive table
  const [editEmpKey, setEditEmpKey] = useState(null);          // unique key of row being edited
  const [editEmpDraft, setEditEmpDraft] = useState({ name: "", id: "", tokens: 0 });
  // Filters for the employee details section
  const [incEmpDate,    setIncEmpDate]   = useState("");   // "" = all dates
  const [incEmpShift,   setIncEmpShift]  = useState("");   // "" | "DAY" | "NIGHT"
  const [incEmpCtor,    setIncEmpCtor]   = useState("");

  // ── Report tab ────────────────────────────────────────────────────────────
  const [rptFrom,      setRptFrom]      = useState(() => { const d=new Date();d.setDate(d.getDate()-6);return d.toISOString().slice(0,10); });
  const [rptTo,        setRptTo]        = useState(today);
  const [rptCtor,      setRptCtor]      = useState("");
  const [reportEntries, setReportEntries] = useState([]); // FIX F3: separate state for report tab
  const [rptLoading,   setRptLoading]   = useState(false);

  // ── Effects ───────────────────────────────────────────────────────────────
  useEffect(() => {
    if (location.search.includes("refresh=1")) {
      showToast("Dashboard refreshed.", "ok");
      window.history.replaceState(null, "", window.location.pathname);
    }
    setIsAdmin(auth?.user?.role === "admin");
  }, []);

  useEffect(() => { fetchEntries(); }, [selDate]);
  // FIX: fetch report data when report tab active or date range changes
  useEffect(() => { if (activeTab === "rpt") fetchReport(); }, [activeTab, rptFrom, rptTo]);

  // ── API: fetch summary for selected date ──────────────────────────────────
  const fetchEntries = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const res   = await axios.get(`${PPC_API_URL}/bravo.php?action=summary&date=${selDate}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.data.success) {
        const mapped = (res.data.contractorSummary || []).map(r => ({
          id:              r.day_entry_id || r.night_entry_id,
          day_entry_id:    r.day_entry_id,
          night_entry_id:  r.night_entry_id,
          contractor:      r.contractor,
          assocF:          parseInt(r.total_assoc_female || 0),
          suppM:           parseInt(r.total_supp_male    || 0),
          suppF:           parseInt(r.total_supp_female  || 0),
          tokens:          parseInt(r.total_tokens       || 0),
          dayHon:          parseFloat(r.day_hon_qty      || 0),
          dayHonCount:     parseInt(r.day_hon_count      || 0),
          nightHon:        parseFloat(r.night_hon_qty    || 0),
          nightHonCount:   parseInt(r.night_hon_count    || 0),
          dayDeh:          deh(parseFloat(r.day_hon_qty  || 0)),
          nightDeh:        deh(parseFloat(r.night_hon_qty|| 0)),
          date:            selDate,
        }));
        // FIX B5: mandatory debug logs
        console.log("Bravo Data (contractorSummary):", res.data.contractorSummary);
        console.log("Bravo Entries (mapped):", mapped);
        console.log("Dashboard Values:", {
          totHON:  mapped.reduce((s,r)=>s+r.dayHon+r.nightHon,0),
          totDH:   mapped.reduce((s,r)=>s+r.dayDeh+r.nightDeh,0),
          totTok:  mapped.reduce((s,r)=>s+(r.tokens||0),0),
          totHONC: mapped.reduce((s,r)=>s+(r.dayHonCount||0)+(r.nightHonCount||0),0),
        });
        setEntries(mapped);
        setTarget(parseFloat(res.data.dayTarget || 0));
        setIsAdmin(res.data.isAdmin || auth?.user?.role === "admin");
      } else {
        console.error("Bravo fetchEntries non-success:", res.data);
        showToast(res.data.error || "Failed to load entries.", "err");
      }
    } catch (err) {
      showToast("Could not load entries. Check network/auth.", "err");
      console.error("Bravo fetchEntries error:", err);
    } finally {
      setLoading(false);
    }
  }, [selDate]);

  // ── API: fetch persisted employee-token details (last 60 days) ────────────
  // Pulls historical emp rows from ppc_bravo_emp_tokens so the Incentive
  // Dashboard reflects existing data (not just newly entered rows).
  const fetchEmpDetails = useCallback(async () => {
    try {
      const token = localStorage.getItem("token");
      const to    = new Date();
      const from  = new Date(); from.setDate(to.getDate() - 60);
      const fromStr = from.toISOString().slice(0, 10);
      const toStr   = to.toISOString().slice(0, 10);

      const res = await axios.get(
        `${PPC_API_URL}/bravo.php?action=emp-tokens&from=${fromStr}&to=${toStr}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (res.data.success && Array.isArray(res.data.rows)) {
        // Map DB rows → state shape used by IncCard, dedupe defensively
        const seen = new Set();
        const mapped = res.data.rows
          .map(r => ({
            submittedAt: r.id,
            name:        r.emp_name   || "Unknown",
            id:          r.emp_id,
            contractor:  r.contractor,
            tokens:      parseInt(r.tokens) || 0,
            date:        r.date,
            shift:       r.shift || "DAY",
          }))
          .filter(e => {
            if (!e.id || !e.date) return false;
            const key = `${e.id}|${e.date}|${e.shift}`;
            if (seen.has(key)) return false;
            seen.add(key);
            return true;
          });
        setEmpDetailsData(mapped);
      }
    } catch (err) {
      console.error("Bravo fetchEmpDetails error:", err);
    }
  }, []);

  // Load persisted emp details once on mount
  useEffect(() => { fetchEmpDetails(); }, [fetchEmpDetails]);

  // ADDED: Edit handler — start inline edit for a row
  const startEditEmp = (e) => {
    const key = `${e.id}|${e.date||""}|${e.shift||"DAY"}`;
    setEditEmpKey(key);
    setEditEmpDraft({ name: e.name||"", id: e.id||"", tokens: parseInt(e.tokens)||0 });
  };
  // ADDED: Edit handler — cancel
  const cancelEditEmp = () => { setEditEmpKey(null); setEditEmpDraft({ name:"", id:"", tokens:0 }); };
  // ADDED: Edit handler — save (POST upserts via existing emp-tokens endpoint)
  const saveEditEmp = async (e) => {
    const tokensNum = parseInt(editEmpDraft.tokens) || 0;
    if (!editEmpDraft.id?.trim() || !editEmpDraft.name?.trim() || tokensNum < 0) {
      showToast("Name, ID, and non-negative tokens required", "warn"); return;
    }
    // Optimistic local update — keep dedup key (id|date|shift) consistent
    setEmpDetailsData(prev => prev.map(row =>
      (row.id===e.id && row.date===e.date && (row.shift||"DAY")===(e.shift||"DAY"))
        ? { ...row, name: editEmpDraft.name.trim(), id: editEmpDraft.id.trim(), tokens: tokensNum }
        : row
    ));
    // Persist to backend
    try {
      const token = localStorage.getItem("token");
      await axios.post(`${PPC_API_URL}/bravo.php?action=emp-tokens`, {
        rows: [{ date: e.date, contractor: e.contractor, shift: e.shift||"DAY",
                 emp_id: editEmpDraft.id.trim(), emp_name: editEmpDraft.name.trim(),
                 tokens: tokensNum }]
      }, { headers: { Authorization: `Bearer ${token}` } });
      showToast("✅ Updated");
    } catch (err) {
      showToast("Update failed — server sync error", "warn");
      console.error("Bravo emp edit error:", err);
    }
    cancelEditEmp();
    fetchEmpDetails();
  };
  // ADDED: Delete handler with confirmation
  const deleteEmp = async (e) => {
    if (!window.confirm(`Delete this employee record?\n\n${e.name||"(no name)"} · ${e.id} · +${(e.tokens||0)-INC_STD} extra tokens`)) return;
    if (!e.submittedAt) {
      // No DB id (legacy/optimistic row) — local-only removal
      setEmpDetailsData(prev => prev.filter(row =>
        !(row.id===e.id && row.date===e.date && (row.shift||"DAY")===(e.shift||"DAY"))
      ));
      showToast("Removed locally", "warn");
      return;
    }
    try {
      const token = localStorage.getItem("token");
      await axios.delete(`${PPC_API_URL}/bravo.php?action=emp-tokens&id=${e.submittedAt}`,
        { headers: { Authorization: `Bearer ${token}` } });
      showToast("🗑 Deleted");
      fetchEmpDetails();
    } catch (err) {
      showToast("Delete failed", "warn");
      console.error("Bravo emp delete error:", err);
    }
  };

  // ── API fetch for report date-range data ──────────────────────────────────
  const fetchReport = useCallback(async () => {
    if (!rptFrom || !rptTo) return;
    setRptLoading(true);
    try {
      const token = localStorage.getItem("token");
      const res   = await axios.get(
        `${PPC_API_URL}/bravo.php?action=report&from=${rptFrom}&to=${rptTo}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.data.success) {
        // FIX B1: support both new (contractorDateSummary) and old (detailedData) response shapes
        const source = res.data.contractorDateSummary || res.data.detailedData || [];
        const mapped = source.map(r => ({
          id:           r.id || `${r.entry_date}_${r.contractor}`,
          contractor:   r.contractor,
          date:         r.entry_date,
          assocF:       parseInt(r.assoc_female  || r.associates_female || 0),
          suppM:        parseInt(r.supp_male     || r.supporting_male   || 0),
          suppF:        parseInt(r.supp_female   || r.supporting_female || 0),
          tokens:       parseInt(r.tokens        || r.total_tokens      || 0),
          dayHon:       parseFloat(r.day_hon_qty || (r.shift === 'DAY'   ? r.hon_quantity : 0) || 0),
          dayHonCount:  parseInt(r.day_hon_count || (r.shift === 'DAY'   ? r.hon_count    : 0) || 0),
          dayDeh:       deh(parseFloat(r.day_hon_qty || (r.shift === 'DAY' ? r.hon_quantity : 0) || 0)),
          nightHon:     parseFloat(r.night_hon_qty || (r.shift === 'NIGHT' ? r.hon_quantity : 0) || 0),
          nightHonCount:parseInt(r.night_hon_count || (r.shift === 'NIGHT' ? r.hon_count    : 0) || 0),
          nightDeh:     deh(parseFloat(r.night_hon_qty || (r.shift === 'NIGHT' ? r.hon_quantity : 0) || 0)),
        }));
        // FIX B5: debug logs for report
        console.log("Bravo Report Raw:", res.data);
        console.log("Bravo Report Mapped:", mapped);
        setReportEntries(mapped);
      } else {
        console.error("Bravo fetchReport non-success:", res.data);
        showToast(res.data.error || "Failed to load report.", "err");
      }
    } catch (err) {
      showToast("Could not load report data.", "err");
      console.error("Bravo fetchReport error:", err);
    } finally {
      setRptLoading(false);
    }
  }, [rptFrom, rptTo]);

  // ── API: save day target ──────────────────────────────────────────────────
  const saveDayTarget = async () => {
    const val = parseFloat(targetInp) || 0;
    setTarget(val);
    try {
      const token = localStorage.getItem("token");
      await axios.post(`${PPC_API_URL}/bravo.php?action=day-target`,
        { date: selDate, target: val },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      showToast("Day target saved!");
    } catch { showToast("Day target saved locally.", "warn"); }
  };

  // ── API: add entry ─────────────────────────────────────────────────────────
  const handleAddSave = async (empRowsFromModal = []) => {
    const f = addForm;
    if (!f.contractor) { showToast("Please select a contractor.", "err"); return; }

    const token   = localStorage.getItem("token");
    const headers = { Authorization: `Bearer ${token}`, "Content-Type": "application/json" };

    const hasDayData     = (parseFloat(f.dayHon)   || 0) > 0 || (parseInt(f.dayHonCount)   || 0) > 0;
    const hasNightData   = (parseFloat(f.nightHon)  || 0) > 0 || (parseInt(f.nightHonCount) || 0) > 0;
    // GENERAL uses dayHon/dayHonCount fields in the form — check the same way
    const hasGeneralData = f.shift === "GENERAL" && hasDayData;
    const hasTokens      = (parseInt(f.tokens) || 0) > 0;
    if (!hasDayData && !hasNightData && !hasTokens) {
      showToast("Enter at least H.ON data or Tokens.", "err"); return;
    }

    // If only tokens entered with no H.ON, default to selected shift or DAY
    const saveAsDayIfTokensOnly = !hasDayData && !hasNightData && hasTokens;

    const base = {
      entry_date:        selDate,
      contractor:        f.contractor,
      working_hours:     parseFloat(f.hrs)     || 8,
      associates_female: parseInt(f.assocF)    || 0,
      supporting_male:   parseInt(f.suppM)     || 0,
      supporting_female: parseInt(f.suppF)     || 0,
    };

    let savedAny = false;
    let saveError = null;

    // Post DAY or GENERAL shift entry (both use dayHon/dayHonCount fields)
    // GENERAL is stored with shift="GENERAL" in the backend — no data mixing
    if (hasDayData || saveAsDayIfTokensOnly) {
      const dayPayload = {
        ...base,
        shift:         f.shift && f.shift !== "NIGHT" ? f.shift : "DAY", // DAY or GENERAL
        tokens:        parseInt(f.tokens) || 0,
        hon_quantity:  parseFloat(f.dayHon)    || 0,
        hon_count:     parseInt(f.dayHonCount) || 0,
        head_less_qty: deh(parseFloat(f.dayHon) || 0),
      };
      console.log(`Bravo Save — ${dayPayload.shift} Payload:`, dayPayload);
      try {
        const res = await axios.post(`${PPC_API_URL}/bravo.php?action=entries`, dayPayload, { headers });
        console.log(`Bravo Save — ${dayPayload.shift} Response:`, res.data);
        if (res.data.success) savedAny = true;
        else saveError = res.data.error || `${dayPayload.shift} shift save failed`;
      } catch (e) {
        saveError = e.response?.data?.error || e.message || `${f.shift||"DAY"} shift network error`;
        console.error(`Bravo Save — ${f.shift||"DAY"} Error:`, e.response?.data || e);
      }
    }

    // Post NIGHT shift entry (tokens=0 to avoid double-counting)
    if (hasNightData) {
      const nightPayload = {
        ...base,
        shift:         "NIGHT",
        tokens:        0,
        hon_quantity:  parseFloat(f.nightHon)    || 0,
        hon_count:     parseInt(f.nightHonCount) || 0,
        head_less_qty: deh(parseFloat(f.nightHon) || 0),
      };
      console.log("Bravo Save — NIGHT Payload:", nightPayload);
      try {
        const res = await axios.post(`${PPC_API_URL}/bravo.php?action=entries`, nightPayload, { headers });
        console.log("Bravo Save — NIGHT Response:", res.data);
        if (res.data.success) savedAny = true;
        else saveError = res.data.error || "Night shift save failed";
      } catch (e) {
        saveError = e.response?.data?.error || e.message || "Night shift network error";
        console.error("Bravo Save — NIGHT Error:", e.response?.data || e);
      }
    }

    if (!savedAny) {
      showToast(saveError || "Save failed — check server.", "err");
      return;
    }

    // Optimistic UI update
    setEntries(prev => {
      const exists = prev.find(e => e.contractor === f.contractor);
      const dayHonAdd   = hasDayData   ? (parseFloat(f.dayHon)    || 0) : 0;
      const dayHCAdd    = hasDayData   ? (parseInt(f.dayHonCount)  || 0) : 0;
      const nightHonAdd = hasNightData ? (parseFloat(f.nightHon)   || 0) : 0;
      const nightHCAdd  = hasNightData ? (parseInt(f.nightHonCount)|| 0) : 0;

      if (exists) {
        return prev.map(e => e.contractor !== f.contractor ? e : {
          ...e,
          tokens:        Math.max(e.tokens || 0, parseInt(f.tokens) || 0),
          dayHon:        (e.dayHon  || 0) + dayHonAdd,
          dayHonCount:   (e.dayHonCount  || 0) + dayHCAdd,
          nightHon:      (e.nightHon || 0) + nightHonAdd,
          nightHonCount: (e.nightHonCount|| 0) + nightHCAdd,
          dayDeh:        deh((e.dayHon   || 0) + dayHonAdd),
          nightDeh:      deh((e.nightHon || 0) + nightHonAdd),
        });
      }
      return [...prev, {
        id:            Date.now(),
        contractor:    f.contractor,
        assocF:        parseInt(f.assocF)    || 0,
        suppM:         parseInt(f.suppM)     || 0,
        suppF:         parseInt(f.suppF)     || 0,
        tokens:        parseInt(f.tokens)    || 0,
        dayHon:        dayHonAdd,
        dayHonCount:   dayHCAdd,
        nightHon:      nightHonAdd,
        nightHonCount: nightHCAdd,
        dayDeh:        deh(dayHonAdd),
        nightDeh:      deh(nightHonAdd),
        date:          selDate,
      }];
    });

    setAddOpen(false);

    // PURE DATA LOG: each form submit adds rows exactly as entered.
    // No auto-deduplication by contractor+date+shift — each submission is its own record.
    // Only deduplicate if the EXACT same record was already saved (same submittedAt key).
    if (empRowsFromModal.length > 0) {
      const cleaned = empRowsFromModal.filter(e => e.name?.trim() || e.eid?.trim());

      // Optimistic local update (existing behavior preserved)
      const newEmps = cleaned.map(e => ({
        submittedAt: Date.now() + Math.random(), // unique per row
        name:        e.name  || "Unknown",
        id:          e.eid   || `EMP-${Date.now()}`,
        contractor:  f.contractor,
        tokens:      parseInt(e.extra || 0) + INC_STD,
        date:        selDate,
        shift:       f.shift || "DAY",
      }));
      if (newEmps.length > 0) {
        setEmpDetailsData(prev => [...prev, ...newEmps]);
      }

      // Persist to backend so data survives reloads / appears for other users
      const payloadRows = cleaned.map(e => ({
        date:       selDate,
        contractor: f.contractor,
        shift:      f.shift || "DAY",
        emp_id:     e.eid   || `EMP-${Date.now()}`,
        emp_name:   e.name  || "Unknown",
        tokens:     (parseInt(e.extra) || 0) + INC_STD,
      }));
      if (payloadRows.length > 0) {
        try {
          await axios.post(
            `${PPC_API_URL}/bravo.php?action=emp-tokens`,
            { rows: payloadRows },
            { headers }
          );
        } catch (err) {
          console.error("Bravo emp-tokens save error:", err);
          showToast("Employee details saved locally — server sync failed", "warn");
        }
      }
    }

    if (saveError) {
      showToast(`Partial save — ${saveError}`, "warn");
    } else {
      showToast("Entry saved! Dashboard updated. ✓");
    }
    // Sync with server (use 1200ms to give DB time to write)
    setTimeout(() => { fetchEntries(); fetchEmpDetails(); }, 1200);
  };

  // ── Edit request submit ───────────────────────────────────────────────────
  const submitEditRequest = () => {
    if (!reqForm.reason?.trim()) { showToast("Please enter a reason.", "err"); return; }
    const entry = entries.find(e => e.id === editTarget?.id);
    if (!entry) return;
    setEditRequests(prev => [...prev, {
      id:          Date.now(),
      entryId:     entry.id,
      contractor:  entry.contractor,
      field:       "Day H.ON / Night H.ON / Tokens",
      oldVal:      `Day:${fmt2(entry.dayHon)} | Night:${fmt2(entry.nightHon)} | Tok:${entry.tokens}`,
      newVal:      `Day:${fmt2(reqForm.dayHon)} | Night:${fmt2(reqForm.nightHon)} | Tok:${reqForm.tokens||entry.tokens}`,
      reason:      reqForm.reason,
      user:        auth?.user?.username || "Operator",
      reqTime:     now8(),
      status:      "pending",
      // Actual values to apply on approval
      _newDayHon:  parseFloat(reqForm.dayHon)      || entry.dayHon,
      _newDayHC:   parseInt(reqForm.dayHonCount)   || entry.dayHonCount,
      _newNightHon:parseFloat(reqForm.nightHon)    || entry.nightHon,
      _newNightHC: parseInt(reqForm.nightHonCount) || entry.nightHonCount,
      _newTokens:  parseInt(reqForm.tokens)        || entry.tokens,
    }]);
    setReqOpen(false);
    showToast("Edit request submitted. Awaiting admin approval. 📤", "warn");
  };

  // ── Admin: approve ────────────────────────────────────────────────────────
  const approveRequest = async (reqId) => {
    const req = editRequests.find(r => r.id === reqId);
    if (!req) return;
    // Apply changes to entries state
    setEntries(prev => prev.map(e => e.id !== req.entryId ? e : {
      ...e,
      dayHon:        req._newDayHon,
      dayHonCount:   req._newDayHC,
      nightHon:      req._newNightHon,
      nightHonCount: req._newNightHC,
      tokens:        req._newTokens,
      dayDeh:        deh(req._newDayHon),
      nightDeh:      deh(req._newNightHon),
    }));
    // Persist to API
    try {
      const token = localStorage.getItem("token");
      await axios.put(`${PPC_API_URL}/bravo.php?action=edit-entry`, {
        entry_id:       req.entryId,
        hon_quantity:   req._newDayHon,
        hon_count:      req._newDayHC,
        head_less_qty:  deh(req._newDayHon),
        tokens:         req._newTokens,
      }, { headers: { Authorization: `Bearer ${token}` } });
    } catch { /* already applied locally */ }
    setEditRequests(prev => prev.map(r => r.id === reqId ? { ...r, status: "approved", reviewedAt: now8() } : r));
    showToast("Edit approved! Dashboard updated. ✓");
  };

  // ── Admin: reject ─────────────────────────────────────────────────────────
  const rejectRequest = (reqId) => {
    setEditRequests(prev => prev.map(r => r.id === reqId ? { ...r, status: "rejected", reviewedAt: now8() } : r));
    showToast("Edit request rejected.", "warn");
  };

  // ── Export CSV ────────────────────────────────────────────────────────────
  const exportCSV = () => {
    const h = ["Contractor","Date","Tokens","Assoc F","Supp M","Supp F",
               "Day H.ON","Day H.ON Count","Day Deheading",
               "Night H.ON","Night H.ON Count","Night Deheading",
               "Total H.ON","Total H.ON Count","Total Deheading"];
    // FIX F5: use reportEntries (multi-date) when on report tab
    const sourceRows = activeTab === "rpt" ? reportEntries : entries;
    const rows = activeTab === "rpt"
      ? sourceRows.filter(e => (!rptCtor || e.contractor === rptCtor))
      : sourceRows;
    const d = rows.map(e => [
      e.contractor, e.date, e.tokens, e.assocF, e.suppM, e.suppF,
      fmt2(e.dayHon), e.dayHonCount, fmt2(e.dayDeh),
      fmt2(e.nightHon), e.nightHonCount, fmt2(e.nightDeh),
      fmt2(e.dayHon+e.nightHon),
      (e.dayHonCount||0)+(e.nightHonCount||0),
      fmt2(e.dayDeh+e.nightDeh),
    ]);
    const csv = [h, ...d].map(r => r.join(",")).join("\n");
    const a = document.createElement("a");
    a.href = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    a.download = `bravo_${activeTab}_${activeTab==="rpt"?`${rptFrom}_to_${rptTo}`:selDate}.csv`;
    a.click();
    showToast("Exported!");
  };

  // ── Toast ─────────────────────────────────────────────────────────────────
  const showToast = (msg, type = "ok") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  };

  // ── Derived values (Bravo tab) ────────────────────────────────────────────
  const totHON  = entries.reduce((s, r) => s + r.dayHon   + r.nightHon,   0);
  const totDH   = entries.reduce((s, r) => s + r.dayDeh   + r.nightDeh,   0);
  const totTok  = entries.reduce((s, r) => s + (r.tokens  || 0),           0);
  const totHONC = entries.reduce((s, r) => s + (r.dayHonCount||0) + (r.nightHonCount||0), 0);
  const bal     = Math.max(0, target - totHON);
  const pendingCount = editRequests.filter(r => r.status === "pending").length;

  // ── Incentive derived ─────────────────────────────────────────────────────
  // empDetailsData contains ONLY explicitly submitted form entries (no statics).
  // Each entry maps directly to one form submission row.
  const incBaseRows = entries.map(r => {
    // FIX: tokens are allocated to WORKING associates only — exclude support
    // (Support Male / Support Female are non-working and not assigned tokens)
    const assoc   = (r.assocF||0);
    const tokAsgn = assoc * INC_STD;
    const extra   = Math.max(0, (r.tokens||0) - tokAsgn);
    // Only use explicitly submitted employee rows for this contractor + date
    const emps    = empDetailsData.filter(
      e => e.contractor === r.contractor && e.date === selDate
    );
    const earners = emps
      .filter(e => (e.tokens || 0) > INC_STD)
      .sort((a, b) => b.tokens - a.tokens);
    return { ...r, assoc, tokAsgn, extra, emps, earners };
  });
  const incRows   = incBaseRows.filter(r => {
    if (incFilter === "inc")  return r.extra > 0;
    if (incFilter === "none") return r.extra === 0;
    return true;
  });
  // FIX: auto-select first contractor so detail panel is never blank
  const incSelRow = incRows.find(r => r.contractor === (incSelCtor || incRows[0]?.contractor)) || incRows[0] || null;

  // ── Report derived ────────────────────────────────────────────────────────
  const rptRows = reportEntries.filter(e =>
    (!rptCtor || e.contractor === rptCtor)
  );
  // FIX B6: rptCtors falls back to entries contractors when reportEntries is empty
  const rptCtors = reportEntries.length > 0
    ? [...new Set(reportEntries.map(r => r.contractor))].sort()
    : [...new Set(entries.map(r => r.contractor))].sort();
  const rptTotHON = rptRows.reduce((s,r) => s + r.dayHon + r.nightHon, 0);
  const rptTotDH  = rptRows.reduce((s,r) => s + r.dayDeh + r.nightDeh, 0);
  const rptTotTok = rptRows.reduce((s,r) => s + (r.tokens||0), 0);
  const rptTotHONC= rptRows.reduce((s,r) => s + (r.dayHonCount||0) + (r.nightHonCount||0), 0);

  // ── Rank colors ───────────────────────────────────────────────────────────
  const rankBg = [
    "linear-gradient(135deg,#f59e0b,#d97706)",
    "linear-gradient(135deg,#9ca3af,#6b7280)",
    "linear-gradient(135deg,#cd7c3a,#b45309)",
  ];

  // ============================================================
  // RENDER
  // ============================================================
  return (
    <div style={S.page}>

      {/* ── Header ── */}
      <header style={S.hdr}>
        <div style={S.hdrL}>
          <button style={S.hbtn} onClick={() => navigate("/ppc")}>← PPC Home</button>
          <span style={S.htitle}>BRAVO DASHBOARD</span>
          <span style={S.hbadge}>👑 Admin</span>
        </div>
        <div style={S.hdrR}>
          <input type="date" value={selDate}
            onChange={e => setSelDate(e.target.value)}
            style={S.hdate} />
          <button style={S.hbtn} onClick={exportCSV}>📥 Export</button>
        </div>
      </header>

      {/* ── Nav tabs ── */}
      <nav style={S.nav}>
        {[
          { key:"bravo",  label:"📊 Bravo Dashboard",       dot:"#1565c0", ac:"#d32f2f"  },
          { key:"inc",    label:"🎯 Incentive Token Details", dot:"#059669", ac:"#059669"  },
          { key:"rpt",    label:"📈 Report Dashboard",        dot:"#7c3aed", ac:"#7c3aed", badge:"Analytics" },
          { key:"admin",  label:"🔐 Admin Panel",             dot:"#d97706", ac:"#d97706", badge: pendingCount > 0 ? String(pendingCount) : null, badgeAlert:true },
        ].map(t => (
          <button key={t.key} style={{ ...S.ntab, ...(activeTab===t.key?{color:t.ac, borderBottom:`3px solid ${t.ac}`}:{}) }}
            onClick={() => setActiveTab(t.key)}>
            <span style={{ width:8, height:8, borderRadius:"50%", background:t.dot, flexShrink:0 }} />
            {t.label}
            {t.badge && (
              <span style={{ background:t.badgeAlert&&pendingCount>0?"#dc2626":t.ac, color:"#fff", fontSize:9, fontWeight:700, padding:"1px 6px", borderRadius:8, animation:t.badgeAlert&&pendingCount>0?"badgePulse 2s infinite":undefined }}>
                {t.badge}
              </span>
            )}
          </button>
        ))}
      </nav>

      {loading && <div style={S.loadBar}><div style={S.loadFill} /></div>}

      <main style={S.main}>

        {/* ══════════════════════════════════════════
            TAB 1 — BRAVO DASHBOARD
        ══════════════════════════════════════════ */}
        {activeTab === "bravo" && (
          <>
            {/* Chip + title */}
            <div>
              <Chip bg="#dbeafe" c="#1e40af" bc="#bfdbfe">📊 SECTION 1 — BRAVO DASHBOARD</Chip>
              <div style={S.secTitle}>Bravo Dashboard</div>
              <div style={S.secSub}>Daily contractor-wise production entries — {selDate}</div>
            </div>

            {/* Period tabs */}
            <div style={S.ptabs}>
              {["daily","weekly","monthly"].map(p => (
                <button key={p} style={{ ...S.ptab, ...(period===p?S.ptabOn:{}) }}
                  onClick={() => setPeriod(p)}>
                  📅 {p[0].toUpperCase()+p.slice(1)}
                </button>
              ))}
            </div>

            {/* 5 KPI cards */}
            <div style={S.kgrid5}>
              {[
                { l:"Day Target",      v: target ? target.toLocaleString()+" KG" : "0 KG", c:"#1565c0", bl:"#1565c0", s:"Target set"           },
                { l:"Total Head ON",   v: fmt2(totHON)+" KG",                              c:"#059669", bl:"#059669", s:"All entries"           },
                { l:"Total Deheading", v: fmt2(totDH)+" KG",                               c:"#059669", bl:"#059669", s:"HON × 0.68"            },
                { l:"Balance",         v: bal.toFixed(2)+" KG",                            c:"#1565c0", bl:"#1565c0", s: bal===0?"On track":"Remaining" },
                { l:"Total Tokens",    v: fmtK(totTok),                                    c:"#d97706", bl:"#d97706", s:"All shifts combined"   },
              ].map((k, i) => (
                <div key={i} style={{ ...S.kc, borderLeft:`4px solid ${k.bl}` }}>
                  <div style={{ ...S.kcLbl, color:k.c }}>{k.l}</div>
                  <div style={{ ...S.kcVal, color:k.c }}>{k.v}</div>
                  <div style={S.kcSub}>{k.s}</div>
                </div>
              ))}
            </div>

            {/* Day target input */}
            <div style={{ display:"flex", alignItems:"center", gap:10 }}>
              <input type="number" placeholder="Enter Day Target (KG)" min={0} step={0.01}
                value={targetInp} onChange={e => setTargetInp(e.target.value)}
                style={{ flex:"0 0 250px", ...S.input }} />
              <button style={S.btnTgt} onClick={saveDayTarget}>Save Target</button>
            </div>

            {/* Entries card */}
            <Card>
              <div style={S.chdr}>
                <span style={S.ctitle}>📋 Entries — {selDate} ({entries.length} records)</span>
                <div style={{ display:"flex", gap:8 }}>
                  <button style={S.btnAdd} onClick={() => { setAddCtor(""); setAddForm(blankAddForm()); setAddOpen(true); }}>
                    ＋ Add Entry
                  </button>
                  <button style={S.btnExp} onClick={exportCSV}>📥 Export</button>
                </div>
              </div>
              <div style={{ overflowX:"auto" }}>
                <table style={{ ...S.tbl, minWidth:1100 }}>
                  <thead>
                    <tr>
                      <th style={{ ...S.th, textAlign:"left", paddingLeft:13 }} rowSpan={2}>Contractor</th>
                      <th style={{ ...S.th, background:"#fffbeb", color:"#92400e" }} rowSpan={2}>Tokens</th>
                      <th style={{ ...S.th, background:"#f0fdf4", color:"#065f46" }}>Associates</th>
                      <th style={{ ...S.th, background:"rgba(21,101,192,.08)", color:"#1565c0" }} colSpan={2}>Support Assoc.</th>
                      {/* Day Shift: 3 cols */}
                      <th style={{ ...S.th, background:"rgba(211,47,47,.08)", color:"#7f1d1d" }} colSpan={3}>Day Shift</th>
                      {/* Night Shift: 3 cols */}
                      <th style={{ ...S.th, background:"rgba(21,101,192,.08)", color:"#1565c0" }} colSpan={3}>Night Shift</th>
                      <th style={{ ...S.th, background:"#e3f2fd", color:"#1565c0", minWidth:90 }} rowSpan={2}>Total H.ON Count</th>
                      <th style={{ ...S.th, background:"#e3f2fd", color:"#1565c0", minWidth:90 }} rowSpan={2}>Total H.ON</th>
                      <th style={{ ...S.th, background:"#d1fae5", color:"#065f46", minWidth:90 }} rowSpan={2}>Total Deheading</th>
                      <th style={{ ...S.th, background:"#f8f9fa" }} rowSpan={2}>Actions</th>
                    </tr>
                    <tr>
                      <th style={{ ...S.th, fontSize:9, background:"#f0fdf4",                  color:"#065f46" }}>Female</th>
                      <th style={{ ...S.th, fontSize:9, background:"rgba(21,101,192,.08)",      color:"#1565c0" }}>Male</th>
                      <th style={{ ...S.th, fontSize:9, background:"rgba(21,101,192,.08)",      color:"#1565c0" }}>Female</th>
                      <th style={{ ...S.th, fontSize:9, background:"rgba(211,47,47,.08)",       color:"#7f1d1d" }}>H.ON Qty (KG)</th>
                      <th style={{ ...S.th, fontSize:9, background:"rgba(211,47,47,.08)",       color:"#7f1d1d" }}>H.ON Count</th>
                      <th style={{ ...S.th, fontSize:9, background:"rgba(211,47,47,.08)",       color:"#7f1d1d" }}>Deheading (KG)</th>
                      <th style={{ ...S.th, fontSize:9, background:"rgba(21,101,192,.08)",      color:"#1565c0" }}>H.ON Qty (KG)</th>
                      <th style={{ ...S.th, fontSize:9, background:"rgba(21,101,192,.08)",      color:"#1565c0" }}>H.ON Count</th>
                      <th style={{ ...S.th, fontSize:9, background:"rgba(21,101,192,.08)",      color:"#1565c0" }}>Deheading (KG)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {entries.length === 0 ? (
                      <tr>
                        <td colSpan={16} style={{ padding:48, textAlign:"center", color:"#9ca3af" }}>
                          <div style={{ fontSize:36, opacity:.3, marginBottom:10 }}>📊</div>
                          <div style={{ fontWeight:600, fontSize:14 }}>No entries for {selDate}</div>
                          <div style={{ fontSize:12, marginTop:4 }}>Click ＋ Add Entry to get started</div>
                        </td>
                      </tr>
                    ) : (
                      entries.map(r => {
                        const totHON2  = r.dayHon  + r.nightHon;
                        const totHONC2 = (r.dayHonCount||0) + (r.nightHonCount||0);
                        const totDH2   = r.dayDeh  + r.nightDeh;
                        return (
                          <tr key={r.id || r.contractor} style={S.tr}
                            onMouseEnter={e=>e.currentTarget.style.background="#fef2f2"}
                            onMouseLeave={e=>e.currentTarget.style.background=""}>
                            <td style={{ ...S.td, textAlign:"left", paddingLeft:13, fontWeight:600, fontFamily:"inherit", fontSize:12 }}>{r.contractor}</td>
                            <td style={S.td}><M v={fmtK(r.tokens)} c="#d97706" w={700} /></td>
                            <td style={S.td}>{r.assocF}</td>
                            <td style={S.td}>{r.suppM}</td>
                            <td style={S.td}>{r.suppF}</td>
                            <td style={S.td}><M v={fmt2(r.dayHon)} /></td>
                            <td style={S.td}>{r.dayHonCount||0}</td>
                            <td style={S.td}><M v={fmt2(r.dayDeh)} c="#d32f2f" /></td>
                            <td style={S.td}><M v={fmt2(r.nightHon)} /></td>
                            <td style={S.td}>{r.nightHonCount||0}</td>
                            <td style={S.td}><M v={fmt2(r.nightDeh)} c="#1565c0" /></td>
                            <td style={{ ...S.td, background:"#e3f2fd" }}>{totHONC2}</td>
                            <td style={{ ...S.td, background:"#e3f2fd" }}><M v={fmt2(totHON2)} c="#1565c0" w={700} /></td>
                            <td style={{ ...S.td, background:"#d1fae5" }}><M v={fmt2(totDH2)} c="#065f46" w={700} /></td>
                            <td style={S.td}>
                              <div style={{ display:"flex", gap:4, justifyContent:"center" }}>
                                <SmBtn bg="#059669" onClick={() => { setAddForm(blankAddForm({contractor:r.contractor,shift:"NIGHT"})); setAddOpen(true); }}>ADD</SmBtn>
                                <SmBtn bg="#d97706" onClick={() => { setEditTarget(r); setReqForm(blankReqForm(r)); setReqOpen(true); }}>EDIT</SmBtn>
                                <SmBtn bg="#dc2626" onClick={async () => {
                                  if (!window.confirm("Delete this entry?")) return;
                                  // FIX B3: delete locally AND refresh from server
                                  setEntries(p => p.filter(e => e.id !== r.id));
                                  showToast("Entry deleted", "warn");
                                  try {
                                    const entryId = r.day_entry_id || r.night_entry_id || r.id;
                                    if (entryId && typeof entryId === 'number') {
                                      const token = localStorage.getItem("token");
                                      await axios.delete(
                                        `${PPC_API_URL}/bravo.php?action=reset&date=${r.date || selDate}`,
                                        { headers: { Authorization: `Bearer ${token}` } }
                                      );
                                    }
                                  } catch (e) { console.error("Delete error:", e); }
                                  setTimeout(fetchEntries, 600);
                                }}>DEL</SmBtn>
                              </div>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                  {entries.length > 0 && (
                    <tfoot>
                      <tr style={S.tfootRow}>
                        <td style={{ ...S.td, textAlign:"left", paddingLeft:13, fontWeight:700, fontFamily:"inherit" }}>TOTAL</td>
                        <td style={S.td}><M v={fmtK(totTok)} c="#d97706" w={700} /></td>
                        {["assocF","suppM","suppF"].map(k => <td key={k} style={S.td}>{entries.reduce((s,r)=>s+(r[k]||0),0)}</td>)}
                        <td style={S.td}><M v={fmt2(entries.reduce((s,r)=>s+r.dayHon,0))} /></td>
                        <td style={S.td}>{entries.reduce((s,r)=>s+(r.dayHonCount||0),0)}</td>
                        <td style={S.td}><M v={fmt2(entries.reduce((s,r)=>s+r.dayDeh,0))} c="#d32f2f" w={700} /></td>
                        <td style={S.td}><M v={fmt2(entries.reduce((s,r)=>s+r.nightHon,0))} /></td>
                        <td style={S.td}>{entries.reduce((s,r)=>s+(r.nightHonCount||0),0)}</td>
                        <td style={S.td}><M v={fmt2(entries.reduce((s,r)=>s+r.nightDeh,0))} c="#1565c0" w={700} /></td>
                        <td style={{ ...S.td, background:"#e3f2fd" }}>{totHONC}</td>
                        <td style={{ ...S.td, background:"#e3f2fd" }}><M v={fmt2(totHON)} c="#1565c0" w={700} /></td>
                        <td style={{ ...S.td, background:"#d1fae5" }}><M v={fmt2(totDH)} c="#065f46" w={700} /></td>
                        <td style={S.td} />
                      </tr>
                    </tfoot>
                  )}
                </table>
              </div>
            </Card>
          </>
        )}

        {/* ══════════════════════════════════════════
            TAB 2 — INCENTIVE TOKEN DETAILS
        ══════════════════════════════════════════ */}
        {activeTab === "inc" && (
          <>
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", flexWrap:"wrap", gap:10 }}>
              <div>
                <Chip bg="#d1fae5" c="#065f46" bc="#6ee7b7">🎯 SECTION 2 — INCENTIVE DETAILS</Chip>
                <div style={S.secTitle}>Incentive Token Details Dashboard</div>
                <div style={S.secSub}>Per-employee extra token breakdown · Standard: {INC_STD} tokens / associate / shift</div>
              </div>
              <button style={{ ...S.btnExp, background:"linear-gradient(135deg,#059669,#1565c0)" }} onClick={() => {
                const csv = [["Contractor","Associates","Tok Assigned","Tok Achieved","Extra Tokens","Earners"],
                  ...incBaseRows.map(r=>[r.contractor,r.assoc,r.tokAsgn,r.tokens,r.extra,r.earners.length])
                ].map(r=>r.join(",")).join("\n");
                const a=document.createElement("a");a.href=URL.createObjectURL(new Blob([csv],{type:"text/csv"}));a.download="bravo_incentive.csv";a.click();
                showToast("Incentive data exported!");
              }}>📊 Export CSV</button>
            </div>

            <IncCard
              rows={incRows} selRow={incSelRow} incFilter={incFilter}
              setIncFilter={setIncFilter} setIncSelCtor={setIncSelCtor}
              incShowEmps={incShowEmps} setIncShowEmps={setIncShowEmps}
              INC_STD={INC_STD} rankBg={rankBg}
              empDetailsData={empDetailsData}
              incEmpDate={incEmpDate}    setIncEmpDate={setIncEmpDate}
              incEmpShift={incEmpShift}  setIncEmpShift={setIncEmpShift}
              incEmpCtor={incEmpCtor}    setIncEmpCtor={setIncEmpCtor}
              allDates={[...new Set(empDetailsData.map(e=>e.date))].sort().reverse()}
              allCtors={[...new Set(empDetailsData.map(e=>e.contractor))].sort()}
              /* ADDED: edit/delete props for incentive table action column */
              editEmpKey={editEmpKey}        editEmpDraft={editEmpDraft}
              setEditEmpDraft={setEditEmpDraft}
              startEditEmp={startEditEmp}    cancelEditEmp={cancelEditEmp}
              saveEditEmp={saveEditEmp}      deleteEmp={deleteEmp}
            />
          </>
        )}

        {/* ══════════════════════════════════════════
            TAB 3 — REPORT DASHBOARD
        ══════════════════════════════════════════ */}
        {activeTab === "rpt" && (
          <>
            {/* Filter bar */}
            <div style={{ background:"#fff", border:"1px solid #e2e6ea", borderRadius:12, padding:"14px 18px", display:"flex", gap:14, alignItems:"flex-end", flexWrap:"wrap" }}>
              {[
                { lbl:"From", el:<input type="date" value={rptFrom} onChange={e=>setRptFrom(e.target.value)} style={S.filterInp} /> },
                { lbl:"To",   el:<input type="date" value={rptTo}   onChange={e=>setRptTo(e.target.value)}   style={S.filterInp} /> },
                { lbl:"Contractor", el:<select value={rptCtor} onChange={e=>setRptCtor(e.target.value)} style={S.filterInp}><option value="">All Contractors</option>{rptCtors.map(c=><option key={c}>{c}</option>)}</select> },
              ].map((f,i) => (
                <div key={i} style={{ display:"flex", flexDirection:"column", gap:4 }}>
                  <span style={S.filterLbl}>{f.lbl}</span>{f.el}
                </div>
              ))}
              {/* FIX F4: explicit fetch button */}
              <button onClick={fetchReport}
                style={{ ...S.btnTgt, alignSelf:"flex-end", padding:"7px 18px", fontSize:12 }}>
                {rptLoading ? "Loading…" : "▶ Fetch Report"}
              </button>
              <button onClick={() => { setRptFrom((() => { const d=new Date();d.setDate(d.getDate()-6);return d.toISOString().slice(0,10); })()); setRptTo(today); setRptCtor(""); }}
                style={{ ...S.btnCancel, alignSelf:"flex-end" }}>✕ Reset</button>
              <button style={{ ...S.btnExp, alignSelf:"flex-end" }} onClick={exportCSV}>📥 Export CSV</button>
              <span style={{ marginLeft:"auto", fontSize:11, color:"#6b7280", alignSelf:"flex-end", paddingBottom:2 }}>{rptRows.length} records</span>
            </div>

            <div>
              <Chip bg="#ede9fe" c="#4c1d95" bc="#c4b5fd">📈 SECTION 3 — REPORT DASHBOARD</Chip>
              <div style={S.secTitle}>Bravo Reports
                <span style={{ display:"inline-flex", alignItems:"center", gap:5, background:"#fef2f2", color:"#7f1d1d", border:"1px solid #fca5a5", padding:"3px 9px", borderRadius:8, fontSize:10, fontWeight:600, marginLeft:8, verticalAlign:"middle" }}>
                  <span style={{ width:6, height:6, borderRadius:"50%", background:"#d32f2f" }} />Live sync
                </span>
              </div>
              <div style={S.secSub}>Analytics — {rptFrom} → {rptTo}</div>
            </div>

            {/* 6 KPI */}
            <div style={S.kgrid6}>
              {[
                { icon:"🏢", l:"Total Contractors", v:rptCtors.length,           c:"#d32f2f", bl:"#d32f2f" },
                { icon:"📦", l:"Total H.ON (KG)",   v:fmt2(rptTotHON)+" KG",     c:"#1565c0", bl:"#1565c0" },
                { icon:"✂️", l:"Total Deheading",  v:fmt2(rptTotDH)+" KG",      c:"#059669", bl:"#059669" },
                { icon:"🔢", l:"Total H.ON Count",  v:fmtK(rptTotHONC),          c:"#7c3aed", bl:"#7c3aed" },
                { icon:"🪙", l:"Total Tokens",      v:fmtK(rptTotTok),           c:"#d97706", bl:"#d97706" },
                { icon:"👷", l:"Total Assoc (F)",   v:rptRows.reduce((s,r)=>s+(r.assocF||0),0), c:"#059669", bl:"#059669" },
              ].map((k,i) => (
                <div key={i} style={{ ...S.kc, borderLeft:`4px solid ${k.bl}`, textAlign:"center" }}>
                  <div style={{ fontSize:20, marginBottom:6 }}>{k.icon}</div>
                  <div style={{ ...S.kcLbl, color:k.c }}>{k.l}</div>
                  <div style={{ ...S.kcVal, color:k.c }}>{k.v}</div>
                </div>
              ))}
            </div>

            {/* Smart insights */}
            {rptRows.length > 0 && (() => {
              const byCtor = rptCtors.map(c => {
                const cr = rptRows.filter(r => r.contractor === c);
                return { c, hon:cr.reduce((s,r)=>s+r.dayHon+r.nightHon,0), dh:cr.reduce((s,r)=>s+r.dayDeh+r.nightDeh,0), tok:cr.reduce((s,r)=>s+(r.tokens||0),0) };
              }).filter(x => x.hon > 0 || x.tok > 0);
              const topHON = [...byCtor].sort((a,b)=>b.hon-a.hon)[0];
              const topDH  = [...byCtor].sort((a,b)=>b.dh-a.dh)[0];
              const topTok = [...byCtor].sort((a,b)=>b.tok-a.tok)[0];
              const lowHON = [...byCtor].sort((a,b)=>a.hon-b.hon)[0];
              return (
                <div style={S.insRow}>
                  {[
                    { ic:"🏆",bg:"#fffbeb",t:"Top by H.ON",   v:topHON?.c||"—", s:`${fmt2(topHON?.hon||0)} KG`, badge:{bg:"#fef3c7",c:"#92400e",t:"Top Performer"} },
                    { ic:"✂️",bg:"#f0fdf4",t:"Top Deheading", v:topDH?.c||"—",  s:`${fmt2(topDH?.dh||0)} KG`,   badge:{bg:"#d1fae5",c:"#065f46",t:"Most Output"}   },
                    { ic:"🪙",bg:"#fffbeb",t:"Top Tokens",    v:topTok?.c||"—", s:`${fmtK(topTok?.tok||0)} tok`,badge:{bg:"#fef3c7",c:"#92400e",t:"Token Leader"}  },
                    { ic:"⚠️",bg:"#fff5f5",t:"Lowest H.ON",  v:lowHON?.c||"—", s:`${fmt2(lowHON?.hon||0)} KG`, badge:{bg:"#fee2e2",c:"#7f1d1d",t:"Needs Review"}  },
                  ].map((s,i) => (
                    <div key={i} style={S.insCard}>
                      <div style={{ width:38, height:38, borderRadius:10, background:s.bg, display:"flex", alignItems:"center", justifyContent:"center", fontSize:17, flexShrink:0 }}>{s.ic}</div>
                      <div>
                        <div style={{ fontSize:10, fontWeight:700, textTransform:"uppercase", letterSpacing:".4px", color:"#6b7280", marginBottom:3 }}>{s.t}</div>
                        <div style={{ fontSize:15, fontWeight:700 }}>{s.v}</div>
                        <div style={{ fontSize:11, color:"#6b7280", marginTop:3 }}>{s.s}</div>
                        <span style={{ display:"inline-flex", fontSize:9, fontWeight:700, padding:"2px 7px", borderRadius:8, marginTop:5, background:s.badge.bg, color:s.badge.c }}>{s.badge.t}</span>
                      </div>
                    </div>
                  ))}
                </div>
              );
            })()}

            {/* Analytics table */}
            <Card>
              <div style={S.chdr}>
                <span style={S.ctitle}>📋 Contractor-wise Analytics Summary</span>
                <button style={S.btnExp} onClick={exportCSV}>📥 Export CSV</button>
              </div>
              <div style={{ overflowX:"auto" }}>
                <table style={S.tbl}>
                  <thead>
                    <tr>
                      {["#","Contractor","Entries","Total Tokens","Day H.ON","Day H.ON Count","Night H.ON","Night H.ON Count","Total H.ON","Total H.ON Count","Total Deheading","Performance"].map((h,i) => (
                        <th key={i} style={{ ...S.th, textAlign:i>1?"right":"left", paddingLeft:i===1?13:undefined, background:"linear-gradient(135deg,#faf5ff,#eff6ff)", color:"#4c1d95", borderBottom:"2px solid #c4b5fd" }}>
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {rptCtors.length === 0 ? (
                      <tr><td colSpan={12} style={{ padding:28, textAlign:"center", color:"#9ca3af" }}>No data for selected filters</td></tr>
                    ) : rptCtors.map((c, i) => {
                      const cr   = rptRows.filter(r => r.contractor === c);
                      if (!cr.length) return null;
                      const dH   = cr.reduce((s,r)=>s+r.dayHon,0);
                      const dHC  = cr.reduce((s,r)=>s+(r.dayHonCount||0),0);
                      const nH   = cr.reduce((s,r)=>s+r.nightHon,0);
                      const nHC  = cr.reduce((s,r)=>s+(r.nightHonCount||0),0);
                      const tH   = dH + nH;
                      const tHC  = dHC + nHC;
                      const tDH  = cr.reduce((s,r)=>s+r.dayDeh+r.nightDeh,0);
                      const tok  = cr.reduce((s,r)=>s+(r.tokens||0),0);
                      const maxH = Math.max(...rptCtors.map(ct=>rptRows.filter(r=>r.contractor===ct).reduce((s,r)=>s+r.dayHon+r.nightHon,0)),1);
                      const pct  = Math.round((tH/maxH)*100);
                      return (
                        <tr key={c} style={S.tr}
                          onMouseEnter={e=>e.currentTarget.style.background="#faf5ff"}
                          onMouseLeave={e=>e.currentTarget.style.background=""}>
                          <td style={{ ...S.td, textAlign:"center" }}>
                            <span style={{ display:"inline-flex", width:22, height:22, borderRadius:"50%", background:rankBg[i]||"linear-gradient(135deg,#7c3aed,#1565c0)", color:"#fff", fontSize:10, fontWeight:700, alignItems:"center", justifyContent:"center" }}>{i+1}</span>
                          </td>
                          <td style={{ ...S.td, textAlign:"left", paddingLeft:13, fontWeight:600, fontFamily:"inherit", fontSize:12 }}>{c}</td>
                          <td style={{ ...S.td, textAlign:"right" }}>{cr.length}</td>
                          <td style={{ ...S.td, textAlign:"right" }}><span style={S.tokPill}>{fmtK(tok)}</span></td>
                          <td style={{ ...S.td, textAlign:"right" }}><M v={fmt2(dH)} c="#1565c0" /></td>
                          <td style={{ ...S.td, textAlign:"right" }}>{dHC}</td>
                          <td style={{ ...S.td, textAlign:"right" }}><M v={fmt2(nH)} c="#1565c0" /></td>
                          <td style={{ ...S.td, textAlign:"right" }}>{nHC}</td>
                          <td style={{ ...S.td, textAlign:"right" }}><M v={fmt2(tH)} c="#1565c0" w={700} /></td>
                          <td style={{ ...S.td, textAlign:"right" }}>{tHC}</td>
                          <td style={{ ...S.td, textAlign:"right" }}><span style={S.dehPill}>{fmt2(tDH)} KG</span></td>
                          <td style={{ ...S.td, paddingLeft:14, paddingRight:14 }}>
                            <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                              <div style={{ flex:1, height:7, background:"#ede9fe", borderRadius:10, overflow:"hidden", minWidth:60 }}>
                                <div style={{ height:"100%", width:`${pct}%`, background:"linear-gradient(90deg,#7c3aed,#60a5fa)", borderRadius:10 }} />
                              </div>
                              <span style={{ fontFamily:"'DM Mono',monospace", fontSize:10, color:"#7c3aed", fontWeight:700, minWidth:32 }}>{pct}%</span>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                  {rptRows.length > 0 && (
                    <tfoot>
                      <tr style={S.tfootRow}>
                        <td /><td style={{ ...S.td, textAlign:"left", paddingLeft:13, fontWeight:700, fontFamily:"inherit" }}>TOTAL</td>
                        <td style={{ ...S.td, textAlign:"right" }}>{rptRows.length}</td>
                        <td style={{ ...S.td, textAlign:"right" }}><span style={S.tokPill}>{fmtK(rptTotTok)}</span></td>
                        <td colSpan={4} style={{ ...S.td, textAlign:"right", color:"#9ca3af" }}>—</td>
                        <td style={{ ...S.td, textAlign:"right" }}><M v={fmt2(rptTotHON)} c="#1565c0" w={700} /></td>
                        <td style={{ ...S.td, textAlign:"right" }}>{rptTotHONC}</td>
                        <td style={{ ...S.td, textAlign:"right" }}><span style={S.dehPill}>{fmt2(rptTotDH)} KG</span></td>
                        <td />
                      </tr>
                    </tfoot>
                  )}
                </table>
              </div>
            </Card>
          </>
        )}

        {/* ══════════════════════════════════════════
            TAB 4 — ADMIN PANEL
        ══════════════════════════════════════════ */}
        {activeTab === "admin" && (
          <>
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", flexWrap:"wrap", gap:10 }}>
              <div>
                <Chip bg="#fffbeb" c="#92400e" bc="#fde68a">🔐 ADMIN PANEL — EDIT APPROVALS</Chip>
                <div style={S.secTitle}>Edit Request Management</div>
                <div style={S.secSub}>Review and approve / reject user edit requests</div>
              </div>
              <div style={{ display:"flex", gap:8 }}>
                {[
                  { l:"Pending",  v:editRequests.filter(r=>r.status==="pending").length,  bg:"#fef3c7", c:"#92400e", bc:"#fde68a" },
                  { l:"Approved", v:editRequests.filter(r=>r.status==="approved").length, bg:"#d1fae5", c:"#065f46", bc:"#6ee7b7" },
                  { l:"Rejected", v:editRequests.filter(r=>r.status==="rejected").length, bg:"#fee2e2", c:"#7f1d1d", bc:"#fca5a5" },
                ].map((s,i) => (
                  <div key={i} style={{ background:s.bg, border:`1px solid ${s.bc}`, borderRadius:10, padding:"8px 16px", textAlign:"center", minWidth:70 }}>
                    <div style={{ fontSize:10, fontWeight:700, textTransform:"uppercase", color:s.c, marginBottom:3 }}>{s.l}</div>
                    <div style={{ fontSize:18, fontWeight:700, fontFamily:"'DM Mono',monospace", color:s.c }}>{s.v}</div>
                  </div>
                ))}
              </div>
            </div>

            <Card>
              <div style={S.chdr}>
                <span style={S.ctitle}>📋 Edit Requests</span>
              </div>
              <div style={{ overflowX:"auto" }}>
                <table style={S.tbl}>
                  <thead>
                    <tr>
                      {["#","Contractor","Requested By","Requested At","Field","Old Value","New Value","Reason","Status","Actions"].map((h,i) => (
                        <th key={i} style={{ ...S.th, background:"linear-gradient(135deg,#fffbeb,#fef3c7)", color:"#92400e", borderBottom:"2px solid #fde68a", textAlign:"left" }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {editRequests.length === 0 ? (
                      <tr><td colSpan={10} style={{ padding:32, textAlign:"center", color:"#9ca3af" }}>No edit requests yet.</td></tr>
                    ) : editRequests.map(req => (
                      <tr key={req.id} style={S.tr}
                        onMouseEnter={e=>e.currentTarget.style.background="#fffbeb"}
                        onMouseLeave={e=>e.currentTarget.style.background=""}>
                        <td style={{ ...S.td, textAlign:"left", fontWeight:700, color:"#6b7280", fontSize:12 }}>#{req.id}</td>
                        <td style={{ ...S.td, textAlign:"left", fontWeight:600 }}>{req.contractor}</td>
                        <td style={{ ...S.td, textAlign:"left", fontSize:11, color:"#6b7280" }}>{req.user}</td>
                        <td style={{ ...S.td, textAlign:"left", fontFamily:"'DM Mono',monospace", fontSize:10, color:"#6b7280" }}>{req.reqTime}</td>
                        <td style={{ ...S.td, textAlign:"left", fontWeight:600, color:"#7c3aed", fontSize:11 }}>{req.field}</td>
                        <td style={{ ...S.td, textAlign:"left", fontFamily:"'DM Mono',monospace", fontSize:10, color:"#d32f2f" }}>{req.oldVal}</td>
                        <td style={{ ...S.td, textAlign:"left", fontFamily:"'DM Mono',monospace", fontSize:10, color:"#059669" }}>{req.newVal}</td>
                        <td style={{ ...S.td, textAlign:"left", fontSize:11 }}>{req.reason}</td>
                        <td style={{ ...S.td, textAlign:"left" }}>
                          <span style={req.status==="pending"?S.sPending:req.status==="approved"?S.sApproved:S.sRejected}>
                            {req.status==="pending"?"⏳ Pending":req.status==="approved"?"✅ Approved":"❌ Rejected"}
                          </span>
                        </td>
                        <td style={{ ...S.td, textAlign:"left" }}>
                          {req.status === "pending" ? (
                            <div style={{ display:"flex", gap:5 }}>
                              <button style={S.approveBtn} onClick={() => approveRequest(req.id)}>✅ Approve</button>
                              <button style={S.rejectBtn}  onClick={() => rejectRequest(req.id)}>❌ Reject</button>
                            </div>
                          ) : (
                            <span style={{ fontSize:11, color:"#9ca3af" }}>Reviewed {req.reviewedAt||""}</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          </>
        )}
      </main>

      {/* ══ ADD ENTRY MODAL ══ */}
      {addOpen && (
        <AddEntryModalBravo
          form={addForm} setForm={setAddForm}
          onClose={() => setAddOpen(false)}
          onSave={handleAddSave}
          selDate={selDate}
        />
      )}

      {/* ══ EDIT REQUEST MODAL ══ */}
      {reqOpen && editTarget && (
        <EditRequestModal
          entry={editTarget} form={reqForm} setForm={setReqForm}
          onClose={() => setReqOpen(false)}
          onSubmit={submitEditRequest}
        />
      )}

      {/* ── Toast ── */}
      {toast && (
        <div style={{ position:"fixed", bottom:22, right:22, padding:"11px 18px", borderRadius:9, color:"#fff", fontSize:13, fontWeight:500, zIndex:999, boxShadow:"0 4px 18px rgba(0,0,0,.28)", background: toast.type==="ok"?"#059669":toast.type==="warn"?"#d97706":"#d32f2f" }}>
          {toast.msg}
        </div>
      )}
    </div>
  );
}

// ═════════════════════════════════════════════════════════
// ADD ENTRY MODAL (new, with H.ON Count + Deheading auto)
// ═════════════════════════════════════════════════════════
function AddEntryModalBravo({ form, setForm, onClose, onSave, selDate }) {
  const [empRows,   setEmpRows]   = useState([]);
  const [etOn,      setEtOn]      = useState(false);
  const [empRid,    setEmpRid]    = useState(0);

  const upd = (k, v) => setForm(f => ({ ...f, [k]: v }));

  // Auto-calc deheading on H.ON change
  const onDayHonChange = v => {
    upd("dayHon", v);
    upd("dayDeh", v > 0 ? fmt2(v * DEHD_FACTOR) : "");
  };
  const onNightHonChange = v => {
    upd("nightHon", v);
    upd("nightDeh", v > 0 ? fmt2(v * DEHD_FACTOR) : "");
  };

  // Shift-aware calculations: NIGHT uses nightHon; DAY and GENERAL both use dayHon
  const isNightShift = form.shift === "NIGHT";
  const activeHon = isNightShift ? (parseFloat(form.nightHon) || 0) : (parseFloat(form.dayHon)  || 0);
  const activeDeh = isNightShift ? (parseFloat(form.nightDeh) || 0) : (parseFloat(form.dayDeh)  || 0);
  const totHON   = activeHon;
  const totDH    = activeDeh;
  const assocCnt = (parseInt(form.assocF)||0)+(parseInt(form.suppM)||0)+(parseInt(form.suppF)||0);
  const incStd   = assocCnt * INC_STD;
  const incTok   = Math.max(0, (parseInt(form.tokens)||0) - incStd);
  const totExtra = empRows.reduce((s,r) => s + (parseInt(r.extra)||0), 0);

  const addEmpRow = () => { setEmpRid(n=>n+1); setEmpRows(r=>[...r,{id:empRid+1,name:"",eid:"",extra:""}]); };
  const delEmpRow = id  => setEmpRows(r=>r.filter(x=>x.id!==id));
  const updEmpRow = (id,k,v) => setEmpRows(r=>r.map(x=>x.id===id?{...x,[k]:v}:x));

  return (
    <div style={S.overlay} onClick={e=>e.target===e.currentTarget&&onClose()}>
      <div style={{ ...S.mbox }} onClick={e=>e.stopPropagation()}>
        <div style={S.mhdr}>
          <div>
            <div style={S.mtitle}>➕ Add Entry — Bravo Dashboard</div>
            <div style={S.msub}>{form.contractor||"—"} · {selDate}</div>
          </div>
          <button style={S.mcls} onClick={onClose}>×</button>
        </div>
        <div style={S.mbody}>

          {/* Tokens & Hours */}
          <FSec bg="#fffbeb" bc="#fde68a">
            <FLbl c="#92400e">🪙 Tokens &amp; Working Hours</FLbl>
            <div style={S.mg3}>
              <FF label="Contractor *">
                <select value={form.contractor} onChange={e=>upd("contractor",e.target.value)} style={S.inp}>
                  <option value="">— Select —</option>
                  {CONTRACTORS.map(c=><option key={c}>{c}</option>)}
                </select>
              </FF>
              <FF label="Total Tokens"><input type="number" value={form.tokens||""} min={0} onChange={e=>upd("tokens",+e.target.value)} style={S.inp} onWheel={e=>e.target.blur()} /></FF>
              <FF label="Working Hours"><input type="number" value={form.hrs||8} min={0} step={0.5} onChange={e=>upd("hrs",+e.target.value)} style={S.inp} onWheel={e=>e.target.blur()} /></FF>
            </div>
          </FSec>

          {/* Shift */}
          <FSec bg="#f0f4ff" bc="#c7d6f7">
            <FLbl c="#1565c0">☀️ Shift</FLbl>
            <div style={{ display:"flex", gap:8 }}>
              {["DAY","GENERAL","NIGHT"].map(s=>(
                <button key={s} type="button"
                  onClick={() => setForm(f => ({
                    ...f,
                    shift: s,
                    // Always reset ALL shift-specific fields when changing shift
                    dayHon:"", dayHonCount:"", dayDeh:"",
                    nightHon:"", nightHonCount:"", nightDeh:"",
                  }))}
                  style={{ flex:1, padding:"9px 0", borderRadius:8,
                    border:`2px solid ${form.shift===s?"#d32f2f":"#e2e6ea"}`,
                    background:form.shift===s?"#fef2f2":"#fff",
                    color:form.shift===s?"#7f1d1d":"#6b7280",
                    fontFamily:"inherit", fontSize:12,
                    fontWeight:form.shift===s?700:500, cursor:"pointer" }}>
                  {s==="DAY"?"☀️ DAY":s==="GENERAL"?"🔄 GENERAL":"🌙 NIGHT"}
                </button>
              ))}
            </div>
            {/* FIX: hint when no shift selected yet */}
            {!form.shift && (
              <div style={{ marginTop:8, fontSize:11, color:"#6b7280", background:"#f8f9fa", border:"1px solid #e2e6ea", borderRadius:7, padding:"7px 10px" }}>
                👆 Select a shift above to enter quantity data
              </div>
            )}
          </FSec>

          {/* Associates */}
          <FSec bg="#fff8f8" bc="#fca5a5">
            <FLbl c="#d32f2f">👷 Associates</FLbl>
            <FF label="Female Associates"><input type="number" value={form.assocF||""} min={0} onChange={e=>upd("assocF",+e.target.value)} style={S.inp} onWheel={e=>e.target.blur()} /></FF>
          </FSec>

          {/* Support Associates */}
          <FSec bg="#eff6ff" bc="#93c5fd">
            <FLbl c="#1565c0">🤝 Support Associates</FLbl>
            <div style={S.mg2}>
              <FF label="Male"><input type="number" value={form.suppM||""} min={0} onChange={e=>upd("suppM",+e.target.value)} style={S.inp} onWheel={e=>e.target.blur()} /></FF>
              <FF label="Female"><input type="number" value={form.suppF||""} min={0} onChange={e=>upd("suppF",+e.target.value)} style={S.inp} onWheel={e=>e.target.blur()} /></FF>
            </div>
          </FSec>

          {/* ── ONE unified Shift Quantity section — renders for DAY, NIGHT, and GENERAL ── */}
          {form.shift && (() => {
            const isNight = form.shift === "NIGHT";
            const isGen   = form.shift === "GENERAL";

            // Visual theme per shift
            const bg     = isNight ? "#eff6ff"  : isGen ? "#f0fdf4"  : "#fff5f5";
            const bc     = isNight ? "#93c5fd"  : isGen ? "#6ee7b7"  : "#fca5a5";
            const lc     = isNight ? "#1565c0"  : isGen ? "#059669"  : "#d32f2f";
            const icon   = isNight ? "🌙" : isGen ? "🔄" : "☀️";
            const bac    = isNight ? "#1565c0"  : "#059669";
            const dehBg  = isNight ? "linear-gradient(135deg,#eff6ff,#dbeafe)"
                                   : isGen ? "linear-gradient(135deg,#f0fdf4,#d1fae5)"
                                           : "linear-gradient(135deg,#ecfdf5,#dbeafe)";
            const dehBc  = isNight ? "#93c5fd"  : "#6ee7b7";
            const dehClr = isNight ? "#1565c0"  : isGen ? "#059669" : "#065f46";
            const hintC  = isNight ? "#1565c0"  : "#059669";

            // Form field keys: NIGHT uses nightHon/nightHonCount/nightDeh
            // DAY and GENERAL both use dayHon/dayHonCount/dayDeh
            const honKey = isNight ? "nightHon"      : "dayHon";
            const hcKey  = isNight ? "nightHonCount" : "dayHonCount";
            const dehKey = isNight ? "nightDeh"      : "dayDeh";

            const onHonChange = v => {
              setForm(f => ({
                ...f,
                [honKey]: v,
                [dehKey]: v > 0 ? fmt2(v * DEHD_FACTOR) : "",
              }));
            };

            return (
              <FSec bg={bg} bc={bc} key={form.shift}>
                <FLbl c={lc}>{icon} {form.shift[0]+form.shift.slice(1).toLowerCase()} Shift Quantity</FLbl>
                <div style={S.mg3}>
                  <FF label="H.ON Count">
                    <input type="number" value={form[hcKey]||""} min={0}
                      onChange={e => upd(hcKey, +e.target.value)}
                      style={S.inp} onWheel={e=>e.target.blur()} />
                  </FF>
                  <FF label="H.ON Qty (KG)">
                    <input type="number" value={form[honKey]||""} min={0} step={0.01}
                      onChange={e => onHonChange(parseFloat(e.target.value)||0)}
                      style={S.inp} onWheel={e=>e.target.blur()} />
                  </FF>
                  <FF label={<span>Deheading (KG) <span style={{ background:bac, color:"#fff", fontSize:8, padding:"1px 5px", borderRadius:5, marginLeft:3 }}>AUTO</span></span>}>
                    <input type="number" value={form[dehKey]||""} readOnly
                      style={{ ...S.inp, background:dehBg, borderColor:dehBc, color:dehClr, fontWeight:700, fontFamily:"'DM Mono',monospace" }} />
                    <span style={{ fontSize:9, color:hintC, marginTop:2, display:"block" }}>= H.ON Qty × 0.68</span>
                  </FF>
                </div>
              </FSec>
            );
          })()}

          {/* Auto calc strip */}
          <FSec bg="#f8f9fa" bc="#e2e6ea">
            <FLbl c="#6b7280">🔢 Auto Calculations</FLbl>
            <div style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:8 }}>
              {[
                { l:"Total H.ON",   v:fmt2(totHON)+" KG", c:"#1565c0" },
                { l:"Total Deheading", v:fmt2(totDH)+" KG",  c:"#059669" },
                { l:"Total Assoc",  v:assocCnt,            c:"#7c3aed" },
              ].map((c,i) => (
                <div key={i} style={{ background:"#fff", borderRadius:8, padding:9, border:"1px solid #e2e6ea", textAlign:"center" }}>
                  <div style={{ fontSize:9, color:"#6b7280", textTransform:"uppercase", letterSpacing:".3px", marginBottom:3 }}>{c.l}</div>
                  <div style={{ fontSize:14, fontWeight:700, fontFamily:"'DM Mono',monospace", color:c.c }}>{c.v}</div>
                </div>
              ))}
            </div>
          </FSec>

          {/* Incentive section */}
          <FSec bg="linear-gradient(135deg,rgba(21,101,192,.03),rgba(5,150,105,.03))" bc="#93c5fd">
            <div style={{ fontSize:12, fontWeight:700, color:"#1565c0", marginBottom:8, display:"flex", justifyContent:"space-between", alignItems:"center" }}>
              <span>📋 Contractor-wise Incentive Token Summary <span style={{ background:"linear-gradient(135deg,#d32f2f,#1565c0)", color:"#fff", fontSize:9, fontWeight:700, padding:"2px 8px", borderRadius:10 }}>New</span></span>
            </div>
            <div style={{ background:"#eff6ff", border:"1px solid #bfdbfe", borderRadius:7, padding:"7px 11px", fontSize:11, color:"#1565c0", marginBottom:10 }}>
              ℹ️ Toggle <strong>Yes</strong> to enter employee-level extra token details.
            </div>
            <div>
              <span style={{ fontSize:11, fontWeight:700, textTransform:"uppercase", letterSpacing:".4px" }}>Extra Tokens? *</span>
              <div style={{ display:"flex", gap:8, marginTop:6 }}>
                <button type="button" onClick={()=>{ setEtOn(true); if(!empRows.length) addEmpRow(); }}
                  style={{ padding:"6px 16px", borderRadius:20, border:`2px solid ${etOn?"#059669":"#e2e6ea"}`, background:etOn?"#ecfdf5":"#fff", color:etOn?"#059669":"#6b7280", fontFamily:"inherit", fontSize:11, fontWeight:700, cursor:"pointer" }}>
                  ✅ Yes
                </button>
                <button type="button" onClick={()=>setEtOn(false)}
                  style={{ padding:"6px 16px", borderRadius:20, border:`2px solid ${!etOn?"#9ca3af":"#e2e6ea"}`, background:!etOn?"#f9fafb":"#fff", color:!etOn?"#9ca3af":"#6b7280", fontFamily:"inherit", fontSize:11, fontWeight:700, cursor:"pointer" }}>
                  ❌ No
                </button>
              </div>
            </div>
            {etOn && (
              <div style={{ marginTop:12 }}>
                <div style={{ fontSize:11, fontWeight:700, color:"#1565c0", marginBottom:8, paddingLeft:8, borderLeft:"3px solid #1565c0" }}>Employee Extra Token Details <span style={{ fontWeight:400, color:"#6b7280" }}>(above {INC_STD} tokens)</span></div>
                {empRows.map((r,i) => (
                  <div key={r.id} style={{ display:"flex", gap:8, alignItems:"flex-end", padding:8, background:"#fff", borderRadius:7, border:"1px solid #e2e6ea", marginBottom:6 }}>
                    <div style={{ width:28, fontWeight:700, color:"#6b7280", paddingBottom:8, textAlign:"center" }}>{i+1}</div>
                    <div style={{ flex:1.3 }}><FF label="Name"><input type="text" value={r.name} onChange={e=>updEmpRow(r.id,"name",e.target.value)} style={{ ...S.inp, fontFamily:"inherit" }} /></FF></div>
                    <div style={{ flex:1 }}><FF label="Emp ID"><input type="text" value={r.eid}  onChange={e=>updEmpRow(r.id,"eid",e.target.value)}  style={{ ...S.inp, fontFamily:"'DM Mono',monospace", fontSize:11 }} /></FF></div>
                    <div style={{ flex:0.8 }}><FF label="Extra Tokens"><input type="number" value={r.extra} min={0} onChange={e=>updEmpRow(r.id,"extra",e.target.value)} style={S.inp} onWheel={e=>e.target.blur()} /></FF></div>
                    <button style={{ width:24, height:24, borderRadius:"50%", border:"1px solid #fca5a5", background:"#fee2e2", color:"#dc2626", fontSize:14, fontWeight:700, cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0, marginBottom:2 }}
                      onClick={()=>delEmpRow(r.id)}>×</button>
                  </div>
                ))}
                <button onClick={addEmpRow} style={{ width:"100%", marginTop:4, padding:7, border:"1.5px dashed #6ee7b7", borderRadius:7, background:"#f0fdf4", color:"#059669", fontFamily:"inherit", fontSize:11, fontWeight:600, cursor:"pointer" }}>
                  ＋ Add Employee Record
                </button>
                <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", background:"#dcfce7", border:"1px solid #6ee7b7", borderRadius:7, padding:"7px 12px", marginTop:8, fontSize:12, fontWeight:600, color:"#065f46" }}>
                  <span>Total Extra Tokens</span>
                  <span style={{ fontFamily:"'DM Mono',monospace", fontSize:16, fontWeight:700 }}>{totExtra > 0 ? `+${totExtra}` : 0}</span>
                </div>
                {/* Incentive calc */}
                <div style={{ background:"linear-gradient(135deg,rgba(245,158,11,.07),rgba(16,185,129,.07))", border:"1px solid #fcd34d", borderRadius:10, padding:12, marginTop:10 }}>
                  <div style={{ fontSize:11, fontWeight:700, color:"#92400e", marginBottom:9 }}>
                    🎯 Token Incentive Calculation <span style={{ background:"#fef3c7", color:"#92400e", fontSize:10, padding:"2px 7px", borderRadius:9, fontWeight:600, marginLeft:5 }}>Standard: {INC_STD} / associate</span>
                  </div>
                  <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:7 }}>
                    {[
                      { l:"Total\nAssociates",  v:assocCnt,              c:"#1565c0" },
                      { l:"Standard\nTokens",   v:incStd,                c:"#1565c0" },
                      { l:"Incentive\nTokens",  v:incTok,                c:"#059669" },
                      { l:"Excess\n/ Assoc",    v:assocCnt>0?(incTok/assocCnt).toFixed(2):"0.00", c:"#d97706" },
                    ].map((c,i) => (
                      <div key={i} style={{ background:"#fff", borderRadius:7, padding:"8px 5px", border:`1px solid ${i===2&&incTok>0?"#6ee7b7":"#e5e7eb"}`, textAlign:"center", background:i===2&&incTok>0?"#ecfdf5":"#fff" }}>
                        <div style={{ fontSize:9, color:"#6b7280", textTransform:"uppercase", letterSpacing:".3px", marginBottom:3, lineHeight:1.4, whiteSpace:"pre" }}>{c.l}</div>
                        <div style={{ fontSize:15, fontWeight:700, fontFamily:"monospace", color:c.c }}>{c.v}</div>
                      </div>
                    ))}
                  </div>
                  <div style={{ marginTop:8, padding:"6px 10px", borderRadius:7, fontSize:11, fontWeight:500, display:"flex", alignItems:"center", gap:5, background:incTok>0?"#d1fae5":"#fef3c7", color:incTok>0?"#065f46":"#92400e" }}>
                    {incTok > 0 ? `✅ ${incTok} incentive tokens earned` : `⚠️ No incentive — tokens didn't exceed standard (${incStd})`}
                  </div>
                </div>
              </div>
            )}
          </FSec>

        </div>
        <div style={S.mfoot}>
          <button style={S.btnCancel} onClick={onClose}>Cancel</button>
          <button style={S.btnSave}   onClick={() => onSave(empRows)}>💾 Save Entry</button>
        </div>
      </div>
    </div>
  );
}

// ═════════════════════════════════════════════════════════
// EDIT REQUEST MODAL
// ═════════════════════════════════════════════════════════
function EditRequestModal({ entry, form, setForm, onClose, onSubmit }) {
  const upd = (k,v) => setForm(f=>({...f,[k]:v}));
  const newDayDH   = deh(parseFloat(form.dayHon)||0);
  const newNightDH = deh(parseFloat(form.nightHon)||0);
  return (
    <div style={S.overlay} onClick={e=>e.target===e.currentTarget&&onClose()}>
      <div style={{ ...S.mbox, maxWidth:560 }} onClick={e=>e.stopPropagation()}>
        <div style={{ ...S.mhdr, background:"linear-gradient(135deg,#d97706,#1565c0)" }}>
          <div>
            <div style={S.mtitle}>✏️ Request Edit — Bravo Dashboard</div>
            <div style={S.msub}>{entry.contractor} · Changes require admin approval</div>
          </div>
          <button style={S.mcls} onClick={onClose}>×</button>
        </div>
        <div style={S.mbody}>
          <div style={{ background:"#fffbeb", border:"1px solid #fde68a", borderRadius:8, padding:"10px 13px", fontSize:11, color:"#92400e" }}>
            ⚠️ You cannot directly edit entries. Submit a request — admin must approve before data is updated.
          </div>
          <FSec bg="#fafafa" bc="#e2e6ea">
            <FLbl c="#6b7280">📋 Entry being edited: <strong style={{ color:"var(--text)" }}>{entry.contractor} — {entry.date}</strong></FLbl>
            <div style={{ fontSize:12, color:"#6b7280" }}>
              Current: Day H.ON = {fmt2(entry.dayHon)} KG · Night H.ON = {fmt2(entry.nightHon)} KG · Tokens = {entry.tokens}
            </div>
          </FSec>
          <FSec bg="#f8f9fa" bc="#e2e6ea">
            <FLbl c="#6b7280">✏️ Requested Changes</FLbl>
            <div style={S.mg2}>
              <FF label="Day H.ON Qty (KG)"><input type="number" value={form.dayHon||""} min={0} step={0.01} onChange={e=>upd("dayHon",e.target.value)} style={S.inp} onWheel={e=>e.target.blur()} /></FF>
              <FF label="Day H.ON Count"><input type="number" value={form.dayHonCount||""} min={0} onChange={e=>upd("dayHonCount",e.target.value)} style={S.inp} onWheel={e=>e.target.blur()} /></FF>
            </div>
            <div style={{ ...S.mg2, marginTop:10 }}>
              <FF label="Night H.ON Qty (KG)"><input type="number" value={form.nightHon||""} min={0} step={0.01} onChange={e=>upd("nightHon",e.target.value)} style={S.inp} onWheel={e=>e.target.blur()} /></FF>
              <FF label="Night H.ON Count"><input type="number" value={form.nightHonCount||""} min={0} onChange={e=>upd("nightHonCount",e.target.value)} style={S.inp} onWheel={e=>e.target.blur()} /></FF>
            </div>
            <div style={{ ...S.mg2, marginTop:10 }}>
              <FF label="Total Tokens"><input type="number" value={form.tokens||""} min={0} onChange={e=>upd("tokens",e.target.value)} style={S.inp} onWheel={e=>e.target.blur()} /></FF>
              <FF label="Reason for Edit *"><input type="text" value={form.reason||""} placeholder="Explain the reason..." onChange={e=>upd("reason",e.target.value)} style={{ ...S.inp, fontFamily:"inherit" }} /></FF>
            </div>
          </FSec>
          {/* Preview diff */}
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10 }}>
            <div style={{ background:"#fff5f5", border:"1px solid #fca5a5", borderRadius:8, padding:10, fontSize:11 }}>
              <div style={{ fontSize:10, fontWeight:700, textTransform:"uppercase", color:"#7f1d1d", marginBottom:6 }}>📌 Current</div>
              Day H.ON: <strong>{fmt2(entry.dayHon)} KG</strong><br/>
              Night H.ON: <strong>{fmt2(entry.nightHon)} KG</strong><br/>
              Deheading: <strong>{fmt2(entry.dayDeh+entry.nightDeh)} KG</strong><br/>
              Tokens: <strong>{entry.tokens}</strong>
            </div>
            <div style={{ background:"#f0fdf4", border:"1px solid #6ee7b7", borderRadius:8, padding:10, fontSize:11 }}>
              <div style={{ fontSize:10, fontWeight:700, textTransform:"uppercase", color:"#065f46", marginBottom:6 }}>✅ Requested</div>
              Day H.ON: <strong>{fmt2(form.dayHon||entry.dayHon)} KG</strong><br/>
              Night H.ON: <strong>{fmt2(form.nightHon||entry.nightHon)} KG</strong><br/>
              Deheading: <strong>{fmt2(newDayDH+newNightDH)} KG</strong><br/>
              Tokens: <strong>{form.tokens||entry.tokens}</strong>
            </div>
          </div>
        </div>
        <div style={S.mfoot}>
          <button style={S.btnCancel} onClick={onClose}>Cancel</button>
          <button style={{ ...S.btnSave, background:"linear-gradient(135deg,#d97706,#1565c0)" }} onClick={onSubmit}>📤 Submit Request</button>
        </div>
      </div>
    </div>
  );
}

// ═════════════════════════════════════════════════════════
// INCENTIVE CARD (extracted component)
// ═════════════════════════════════════════════════════════
function IncCard({
  rows, selRow, incFilter, setIncFilter, setIncSelCtor,
  incShowEmps, setIncShowEmps, INC_STD, rankBg,
  empDetailsData, incEmpDate, setIncEmpDate,
  incEmpShift, setIncEmpShift, incEmpCtor, setIncEmpCtor,
  allDates, allCtors,
  /* ADDED: edit/delete props */
  editEmpKey, editEmpDraft, setEditEmpDraft,
  startEditEmp, cancelEditEmp, saveEditEmp, deleteEmp,
}) {
  const totA    = rows.reduce((s,r)=>s+r.assoc,0);
  const totAsn  = rows.reduce((s,r)=>s+r.tokAsgn,0);
  const totAchv = rows.reduce((s,r)=>s+r.tokens,0);
  const totEx   = rows.reduce((s,r)=>s+r.extra,0);

  // PURE DATA LOG: empDetailsData contains ONLY explicitly submitted form rows.
  // No auto-fill, no static data, no merge logic — just what the user entered.
  const filteredEmps = (empDetailsData||[]).filter(e => {
    if (incEmpDate  && e.date       !== incEmpDate)  return false;
    if (incEmpShift && e.shift      !== incEmpShift) return false;
    if (incEmpCtor  && e.contractor !== incEmpCtor)  return false;
    return true;
  }).sort((a, b) => b.tokens - a.tokens);

  const empEarners  = filteredEmps.filter(e => (e.tokens || 0) > INC_STD);
  const empExcluded = filteredEmps.length - empEarners.length;

  const selInp = { padding:"6px 10px", borderRadius:7, border:"1.5px solid #6ee7b7", fontFamily:"inherit", fontSize:11, background:"#fff", outline:"none", cursor:"pointer", minWidth:120 };

  return (
    <div style={{ borderRadius:12, overflow:"hidden", border:"1.5px solid #6ee7b7", boxShadow:"0 2px 12px rgba(5,150,105,.1)" }}>
      {/* Gradient header */}
      <div style={{ background:"linear-gradient(135deg,#059669,#1565c0)", padding:"14px 18px", display:"flex", justifyContent:"space-between", alignItems:"center", flexWrap:"wrap", gap:10 }}>
        <div>
          <div style={{ color:"#fff", fontSize:14, fontWeight:700 }}>🎯 Incentive Token Details</div>
          <div style={{ color:"rgba(255,255,255,.78)", fontSize:11, marginTop:2 }}>Click a contractor to view employee-level breakdown</div>
        </div>
        <div style={{ background:"rgba(211,47,47,.3)", border:"1px solid rgba(255,255,255,.3)", borderRadius:8, padding:"6px 13px", fontSize:11, fontWeight:700, color:"#fff" }}>
          🎯 Standard: {INC_STD} tokens / associate
        </div>
      </div>

      {/* Summary filter */}
      <div style={{ background:"#f0fdf4", borderBottom:"1px solid #bbf7d0", padding:"11px 16px", display:"flex", gap:10, alignItems:"flex-end" }}>
        <div>
          <label style={{ display:"block", fontSize:10, fontWeight:700, color:"#065f46", textTransform:"uppercase", letterSpacing:".4px", marginBottom:4 }}>SHOW</label>
          <select value={incFilter} onChange={e=>setIncFilter(e.target.value)}
            style={{ padding:"7px 10px", borderRadius:7, border:"1.5px solid #6ee7b7", fontFamily:"inherit", fontSize:12, background:"#fff", outline:"none", minWidth:150, cursor:"pointer" }}>
            <option value="">All Contractors</option>
            <option value="inc">With Incentive Only</option>
            <option value="none">No Incentive Only</option>
          </select>
        </div>
        <button onClick={()=>setIncFilter("")} style={{ padding:"7px 11px", borderRadius:7, border:"1.5px solid #6ee7b7", background:"#fff", color:"#059669", fontFamily:"inherit", fontSize:12, cursor:"pointer", fontWeight:600 }}>✕ Clear</button>
      </div>

      {/* Stat strip */}
      <div style={{ display:"grid", gridTemplateColumns:"repeat(5,1fr)", background:"#fff", borderBottom:"1px solid #bbf7d0" }}>
        {[
          { l:"🏢 CONTRACTORS",     v: rows.length || 0,                         c:"#1565c0" },
          { l:"👷 ASSOCIATES",      v: totA || 0,                                 c:"#1565c0" },
          { l:"📋 TOKENS ASSIGNED", v: fmtK(totAsn),                             c:"#d97706" },
          { l:"✅ TOKENS ACHIEVED", v: fmtK(totAchv),                            c:"#1565c0" },
          { l:"🎯 INCENTIVE TOKENS",v: (totEx>0?"+":"")+fmtK(totEx),            c: totEx>0?"#059669":"#9ca3af" },
        ].map((s,i) => (
          <div key={i} style={{ padding:"13px 15px", borderRight:i<4?"1px solid #bbf7d0":"none" }}>
            <div style={{ fontSize:10, color:"#6b7280", textTransform:"uppercase", letterSpacing:".4px", marginBottom:4 }}>{s.l}</div>
            <div style={{ fontSize:17, fontWeight:700, fontFamily:"'DM Mono',monospace", color:s.c }}>{s.v}</div>
          </div>
        ))}
      </div>

      {/* Contractor pills */}
      <div style={{ background:"#f0fdf4", borderBottom:"1px solid #bbf7d0", padding:"12px 16px" }}>
        <div style={{ fontSize:9, fontWeight:700, color:"#059669", textTransform:"uppercase", letterSpacing:".5px", marginBottom:10 }}>🏢 SELECT CONTRACTOR TO VIEW EMPLOYEE DETAILS</div>
        {rows.length === 0 ? (
          <div style={{ fontSize:12, color:"#9ca3af", padding:"8px 0" }}>No data available — add entries to see incentive details.</div>
        ) : (
          <div style={{ display:"flex", flexWrap:"wrap", gap:8 }}>
            {rows.map(r => {
              const isAct = selRow?.contractor === r.contractor;
              return (
                <button key={r.contractor}
                  style={{ display:"flex", alignItems:"center", gap:7, padding:"7px 14px", borderRadius:20, border:`1.5px solid ${isAct?"#059669":"#bbf7d0"}`, background:isAct?"linear-gradient(135deg,#ecfdf5,#dbeafe)":"#fff", cursor:"pointer", fontFamily:"inherit", fontSize:12, fontWeight:isAct?700:500, color:isAct?"#059669":"#1a1d23", boxShadow:isAct?"0 2px 10px rgba(5,150,105,.15)":"none" }}
                  onClick={() => setIncSelCtor(r.contractor)}>
                  <span style={{ width:9, height:9, borderRadius:"50%", background:r.extra>0?"#059669":"#9ca3af", flexShrink:0 }} />
                  {r.contractor}
                  {r.earners.length > 0 && <span style={{ background:"#059669", color:"#fff", fontSize:9, fontWeight:700, padding:"1px 6px", borderRadius:8 }}>+{r.earners.length}</span>}
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* Detail panel for selected contractor */}
      {selRow && (
        <div style={{ background:"#fff" }}>
          <div style={{ display:"grid", gridTemplateColumns:"repeat(5,1fr)", padding:"16px 18px", background:"linear-gradient(135deg,#f0fdf4,#eff6ff)", borderBottom:"1px solid #bbf7d0", gap:12 }}>
            {[
              { l:"🏢 Contractor",      v:selRow.contractor,         vc:"#1a1d23", sub:<span style={{ padding:"3px 9px", borderRadius:8, fontSize:10, fontWeight:700, background:"#fef3c7", color:"#e65100" }}>DAY</span> },
              { l:"👷 Associates",      v:selRow.assoc,              vc:"#1565c0", sub:`${selRow.emps.length} records` },
              { l:"📋 Tokens Assigned", v:fmtK(selRow.tokAsgn),      vc:"#d97706", sub:`${selRow.assoc}×${INC_STD}` },
              { l:"✅ Tokens Achieved", v:fmtK(selRow.tokens),       vc:"#1565c0", sub:`${selRow.tokAsgn>0?Math.round(selRow.tokens/selRow.tokAsgn*100):0}% of standard`, subc:selRow.tokens>=selRow.tokAsgn?"#059669":"#d97706" },
              { l:"🎯 Extra Tokens",    v:null,
                node: selRow.extra>0
                  ? <span style={{ display:"inline-flex", background:"linear-gradient(135deg,#ecfdf5,#d1fae5)", border:"1.5px solid #6ee7b7", color:"#065f46", borderRadius:20, padding:"5px 14px", fontSize:14, fontWeight:700, fontFamily:"'DM Mono',monospace" }}>+{selRow.extra}</span>
                  : <span style={{ display:"inline-flex", background:"#f9fafb", border:"1px solid #e5e7eb", color:"#9ca3af", borderRadius:20, padding:"5px 12px", fontSize:12 }}>No incentive</span>,
                sub: selRow.extra>0 ? `${selRow.earners.length} employees earned` : "Standard not exceeded",
                subc: selRow.extra>0 ? "#059669" : "#9ca3af",
              },
            ].map((cell,i) => (
              <div key={i} style={{ textAlign:"center" }}>
                <div style={{ fontSize:10, color:"#6b7280", fontWeight:600, textTransform:"uppercase", letterSpacing:".3px", marginBottom:6 }}>{cell.l}</div>
                {cell.node
                  ? <div style={{ marginTop:4 }}>{cell.node}</div>
                  : <div style={{ fontSize:17, fontWeight:700, fontFamily:"'DM Mono',monospace", color:cell.vc }}>{cell.v}</div>
                }
                {cell.sub && <div style={{ fontSize:10, color:cell.subc||"#6b7280", fontWeight:600, marginTop:3 }}>{typeof cell.sub==="string"?cell.sub:<div>{cell.sub}</div>}</div>}
              </div>
            ))}
          </div>

          {/* Toggle button — always visible for any selected row */}
          {selRow && (
            <button
              style={{ display:"inline-flex", alignItems:"center", gap:6, padding:"9px 20px", margin:"12px 18px", borderRadius:8, border:incShowEmps?"none":"1.5px solid #6ee7b7", background:incShowEmps?"linear-gradient(135deg,#059669,#065f46)":"#ecfdf5", color:incShowEmps?"#fff":"#059669", fontFamily:"inherit", fontSize:13, fontWeight:700, cursor:"pointer" }}
              onClick={() => setIncShowEmps(v=>!v)}>
              {incShowEmps ? "🙈 Hide Incentive Employee Details" : "👁 Show Incentive Employee Details"}
            </button>
          )}

          {/* Employee details section — default visible */}
          {selRow && incShowEmps && (
            <div style={{ padding:"0 18px 18px" }}>

              {/* ── 3 Filters: Date, Shift, Contractor ── */}
              <div style={{ display:"flex", gap:10, alignItems:"flex-end", flexWrap:"wrap", padding:"10px 0 14px", borderBottom:"1px solid #e2e6ea", marginBottom:12 }}>
                <div>
                  <label style={{ display:"block", fontSize:10, fontWeight:700, color:"#6b7280", textTransform:"uppercase", letterSpacing:".4px", marginBottom:4 }}>📅 Date</label>
                  <select value={incEmpDate} onChange={e=>setIncEmpDate(e.target.value)} style={selInp}>
                    <option value="">All Dates</option>
                    {(allDates||[]).map(d=><option key={d} value={d}>{d}</option>)}
                  </select>
                </div>
                <div>
                  <label style={{ display:"block", fontSize:10, fontWeight:700, color:"#6b7280", textTransform:"uppercase", letterSpacing:".4px", marginBottom:4 }}>⏰ Shift</label>
                  <select value={incEmpShift} onChange={e=>setIncEmpShift(e.target.value)} style={selInp}>
                    <option value="">All Shifts</option>
                    <option value="DAY">☀️ Day</option>
                    <option value="NIGHT">🌙 Night</option>
                    <option value="GENERAL">🔄 General</option>
                  </select>
                </div>
                <div>
                  <label style={{ display:"block", fontSize:10, fontWeight:700, color:"#6b7280", textTransform:"uppercase", letterSpacing:".4px", marginBottom:4 }}>🏢 Contractor</label>
                  <select value={incEmpCtor} onChange={e=>setIncEmpCtor(e.target.value)} style={selInp}>
                    <option value="">All Contractors</option>
                    {(allCtors||[]).map(c=><option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <button
                  onClick={()=>{ setIncEmpDate(""); setIncEmpShift(""); setIncEmpCtor(""); }}
                  style={{ padding:"6px 12px", borderRadius:7, border:"1.5px solid #e2e6ea", background:"#fff", color:"#6b7280", fontFamily:"inherit", fontSize:11, cursor:"pointer", fontWeight:600 }}>
                  ✕ Reset Filters
                </button>
                <span style={{ marginLeft:"auto", fontSize:11, color:"#6b7280", alignSelf:"flex-end", paddingBottom:2 }}>
                  {empEarners.length} earner{empEarners.length!==1?"s":""} · {empExcluded} excluded
                </span>
              </div>

              {/* Info bar */}
              <div style={{ background:"#eff6ff", border:"1px solid #bfdbfe", borderRadius:8, padding:"9px 13px", fontSize:11, color:"#1565c0", marginBottom:12, display:"flex", gap:6, alignItems:"center", flexWrap:"wrap" }}>
                ℹ️ Only employees who completed <strong style={{ margin:"0 3px" }}>more than {INC_STD} tokens</strong>.
                {empExcluded > 0 && <span>{empExcluded} below standard excluded.</span>}
              </div>

              {/* Employee table — shows ONLY explicitly submitted form entries */}
              {empEarners.length === 0 ? (
                <div style={{ padding:"28px", textAlign:"center", color:"#9ca3af", background:"#f9fafb", borderRadius:8, border:"1px solid #e2e6ea" }}>
                  <div style={{ fontSize:28, opacity:.3, marginBottom:8 }}>👥</div>
                  <div style={{ fontWeight:600, fontSize:13 }}>No Data Available</div>
                  <div style={{ fontSize:11, marginTop:4 }}>Submit entries with employee token details via the Add Entry form to see records here.</div>
                  {filteredEmps.length > 0 && (
                    <div style={{ marginTop:8, fontSize:11, color:"#d97706", background:"#fffbeb", border:"1px solid #fde68a", borderRadius:7, padding:"6px 12px", display:"inline-block" }}>
                      ⚠️ {filteredEmps.length} record{filteredEmps.length!==1?"s":""} exist below the {INC_STD}-token standard
                    </div>
                  )}
                </div>
              ) : (
                <table style={{ width:"100%", borderCollapse:"collapse", fontSize:11 }}>
                  <thead>
                    <tr>{["#","Employee Name","Employee ID","Tokens Done",`Standard (${INC_STD})`, "Extra Tokens","Progress","Actions"].map((h,i)=>(
                      <th key={i} style={{ padding:"9px 12px", fontWeight:700, fontSize:10, background:"#f8f9fa", border:"1px solid #e2e6ea", textAlign:i===0||i===7?"center":i>=3&&i<=5?"right":"left", textTransform:"uppercase", letterSpacing:".3px" }}>{h}</th>
                    ))}</tr>
                  </thead>
                  <tbody>
                    {empEarners.map((e,i) => {
                      const ex = (e.tokens || 0) - INC_STD;
                      const pbar = Math.min(100, Math.round(ex/INC_STD*100));
                      // ADDED: inline edit detection
                      const rowKey = `${e.id}|${e.date||""}|${e.shift||"DAY"}`;
                      const isEdit = editEmpKey === rowKey;
                      return (
                        <tr key={e.submittedAt || e.id || i}
                          style={isEdit ? { background:"#fffbeb", borderLeft:"4px solid #d97706" } : { borderBottom:"1px solid #f1f5f9" }}
                          onMouseEnter={ev=>{ if (!isEdit) ev.currentTarget.style.background="#f0fdf4"; }}
                          onMouseLeave={ev=>{ if (!isEdit) ev.currentTarget.style.background=""; }}>
                          <td style={{ padding:"9px 12px", textAlign:"center" }}>
                            <span style={{ display:"inline-flex", width:24, height:24, borderRadius:"50%", background:rankBg[i]||"linear-gradient(135deg,#7c3aed,#1565c0)", color:"#fff", fontSize:10, fontWeight:700, alignItems:"center", justifyContent:"center" }}>{i+1}</span>
                          </td>
                          {/* ADDED: editable Name */}
                          <td style={{ padding:"9px 12px", fontWeight:600 }}>
                            {isEdit ? (
                              <input type="text" value={editEmpDraft.name}
                                onChange={ev=>setEditEmpDraft(d=>({ ...d, name: ev.target.value }))}
                                style={{ width:"100%", padding:"4px 8px", border:"1px solid #d1d5db", borderRadius:5, fontSize:11, fontFamily:"inherit" }} />
                            ) : e.name}
                          </td>
                          {/* ADDED: editable ID */}
                          <td style={{ padding:"9px 12px", fontFamily:"'DM Mono',monospace", fontSize:11, color:"#6b7280" }}>
                            {isEdit ? (
                              <input type="text" value={editEmpDraft.id}
                                onChange={ev=>setEditEmpDraft(d=>({ ...d, id: ev.target.value }))}
                                style={{ width:"100%", padding:"4px 8px", border:"1px solid #d1d5db", borderRadius:5, fontSize:11, fontFamily:"'DM Mono',monospace" }} />
                            ) : e.id}
                          </td>
                          {/* ADDED: editable Tokens */}
                          <td style={{ padding:"9px 12px", textAlign:"right", fontWeight:700, color:"#1565c0", fontFamily:"'DM Mono',monospace" }}>
                            {isEdit ? (
                              <input type="number" min="0" value={editEmpDraft.tokens}
                                onChange={ev=>setEditEmpDraft(d=>({ ...d, tokens: ev.target.value }))}
                                style={{ width:"100%", padding:"4px 8px", border:"1px solid #d1d5db", borderRadius:5, fontSize:11, textAlign:"right", fontFamily:"'DM Mono',monospace" }} />
                            ) : e.tokens}
                          </td>
                          <td style={{ padding:"9px 12px", textAlign:"right", color:"#9ca3af", fontFamily:"'DM Mono',monospace" }}>{INC_STD}</td>
                          <td style={{ padding:"9px 12px", textAlign:"right" }}>
                            <span style={{ background:"#ecfdf5", border:"1px solid #6ee7b7", color:"#065f46", borderRadius:12, padding:"2px 9px", fontSize:11, fontWeight:700, fontFamily:"'DM Mono',monospace" }}>+{ex}</span>
                          </td>
                          <td style={{ padding:"9px 12px" }}>
                            {isEdit ? (
                              <span style={{ color:"#9ca3af", fontStyle:"italic", fontSize:10 }}>Editing…</span>
                            ) : (
                              <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                                <div style={{ flex:1, height:7, background:"#e5e7eb", borderRadius:10, overflow:"hidden", minWidth:60 }}>
                                  <div style={{ height:"100%", width:`${pbar}%`, background:"linear-gradient(90deg,#059669,#34d399)", borderRadius:10 }} />
                                </div>
                                <span style={{ fontFamily:"'DM Mono',monospace", fontSize:10, color:"#059669", fontWeight:700, minWidth:28 }}>+{ex}</span>
                              </div>
                            )}
                          </td>
                          {/* ADDED: Action cell */}
                          <td style={{ padding:"9px 12px", textAlign:"center" }}>
                            {isEdit ? (
                              <div style={{ display:"inline-flex", gap:6 }}>
                                <button onClick={()=>saveEditEmp(e)}
                                  style={{ padding:"4px 10px", borderRadius:5, border:"none", background:"#059669", color:"#fff", fontSize:10, fontWeight:700, cursor:"pointer", fontFamily:"inherit" }}>SAVE</button>
                                <button onClick={cancelEditEmp}
                                  style={{ padding:"4px 10px", borderRadius:5, border:"1px solid #d1d5db", background:"#fff", color:"#374151", fontSize:10, fontWeight:700, cursor:"pointer", fontFamily:"inherit" }}>CANCEL</button>
                              </div>
                            ) : (
                              <div style={{ display:"inline-flex", gap:6 }}>
                                <button onClick={()=>startEditEmp(e)}
                                  style={{ padding:"4px 10px", borderRadius:5, border:"none", background:"#1565c0", color:"#fff", fontSize:10, fontWeight:700, cursor:"pointer", fontFamily:"inherit" }}>EDIT</button>
                                <button onClick={()=>deleteEmp(e)}
                                  style={{ padding:"4px 10px", borderRadius:5, border:"none", background:"#d32f2f", color:"#fff", fontSize:10, fontWeight:700, cursor:"pointer", fontFamily:"inherit" }}>DEL</button>
                              </div>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr style={{ background:"#f0f2f5", borderTop:"2px solid #e2e6ea" }}>
                      <td colSpan={4} style={{ padding:"9px 12px", fontWeight:700 }}>TOTAL — {empEarners.length} employee{empEarners.length!==1?"s":""} above standard</td>
                      <td style={{ padding:"9px 12px", textAlign:"right", color:"#9ca3af", fontFamily:"'DM Mono',monospace", fontWeight:700 }}>{empEarners.length*INC_STD}</td>
                      <td style={{ padding:"9px 12px", textAlign:"right" }}>
                        <span style={{ background:"#ecfdf5", border:"1px solid #6ee7b7", color:"#065f46", borderRadius:12, padding:"2px 9px", fontSize:11, fontWeight:700, fontFamily:"'DM Mono',monospace" }}>
                          +{empEarners.reduce((s,e)=>s+(e.tokens-INC_STD),0)}
                        </span>
                      </td>
                      <td />
                      <td />{/* ADDED: empty cell aligning with Action column */}
                    </tr>
                  </tfoot>
                </table>
              )}
            </div>
          )}
        </div>
      )}

      {/* Empty state when no contractor is selected */}
      {!selRow && rows.length === 0 && (
        <div style={{ padding:"42px", textAlign:"center", color:"#9ca3af" }}>
          <div style={{ fontSize:36, opacity:.3, marginBottom:10 }}>📊</div>
          <div style={{ fontWeight:600, fontSize:14 }}>No entries for this date</div>
          <div style={{ fontSize:12, marginTop:4 }}>Switch to Bravo Dashboard and add entries with tokens to see incentive data.</div>
        </div>
      )}
    </div>
  );
}

// ═════════════════════════════════════════════════════════
// SMALL HELPERS
// ═════════════════════════════════════════════════════════
function blankAddForm(defaults={}) {
  return { contractor:"", shift:"", tokens:"", hrs:8, assocF:"", suppM:"", suppF:"",
           dayHon:"", dayHonCount:"", dayDeh:"", nightHon:"", nightHonCount:"", nightDeh:"", ...defaults };
}
function blankReqForm(entry={}) {
  return { dayHon:entry.dayHon||"", dayHonCount:entry.dayHonCount||"",
           nightHon:entry.nightHon||"", nightHonCount:entry.nightHonCount||"",
           tokens:entry.tokens||"", reason:"" };
}

const Chip = ({ bg, c, bc, children }) => (
  <span style={{ display:"inline-flex", alignItems:"center", gap:6, background:bg, color:c, border:`1px solid ${bc}`, fontSize:10, fontWeight:700, padding:"3px 10px", borderRadius:12, letterSpacing:".4px" }}>
    {children}
  </span>
);
const Card     = ({ children }) => <div style={S.card}>{children}</div>;
const FSec     = ({ bg, bc, children }) => <div style={{ borderRadius:10, padding:12, background:bg, border:`1px solid ${bc}` }}>{children}</div>;
const FLbl     = ({ c, children }) => <div style={{ fontSize:11, fontWeight:700, marginBottom:9, color:c||"#1565c0" }}>{children}</div>;
const FF       = ({ label, children }) => <div><label style={{ display:"block", fontSize:10, fontWeight:700, color:"#6b7280", marginBottom:4, textTransform:"uppercase", letterSpacing:".4px" }}>{label}</label>{children}</div>;
const SmBtn    = ({ bg, onClick, children }) => <button style={{ padding:"3px 8px", border:"none", borderRadius:5, color:"#fff", fontSize:10, fontWeight:700, cursor:"pointer", background:bg, fontFamily:"inherit" }} onClick={onClick}>{children}</button>;
const M        = ({ v, c="#1a1d23", w=600 }) => <span style={{ fontFamily:"'DM Mono',monospace", fontSize:11, color:c, fontWeight:w }}>{v}</span>;

// ═════════════════════════════════════════════════════════
// STYLES
// ═════════════════════════════════════════════════════════
const S = {
  page:       { fontFamily:"'DM Sans',sans-serif", background:"#f0f2f5", minHeight:"100vh", fontSize:13, paddingBottom:60, color:"#1a1d23" },
  hdr:        { background:GRAD, height:52, padding:"0 20px", display:"flex", alignItems:"center", justifyContent:"space-between", position:"sticky", top:0, zIndex:300, boxShadow:"0 2px 14px rgba(211,47,47,.3)" },
  hdrL:       { display:"flex", alignItems:"center", gap:10 },
  hdrR:       { display:"flex", alignItems:"center", gap:8 },
  hbtn:       { background:"rgba(255,255,255,.15)", border:"1px solid rgba(255,255,255,.28)", color:"#fff", padding:"5px 12px", borderRadius:6, cursor:"pointer", fontSize:12, fontFamily:"inherit", fontWeight:500 },
  htitle:     { color:"#fff", fontSize:14, fontWeight:700, letterSpacing:".3px" },
  hbadge:     { background:"rgba(255,255,255,.2)", color:"#fff", fontSize:10, padding:"2px 9px", borderRadius:20 },
  hdate:      { background:"rgba(255,255,255,.95)", border:"none", borderRadius:6, padding:"4px 9px", fontSize:12, fontFamily:"inherit", color:"#1a1d23", cursor:"pointer", outline:"none" },
  nav:        { background:"#fff", borderBottom:"2px solid #e2e6ea", padding:"0 20px", display:"flex", alignItems:"center", position:"sticky", top:52, zIndex:200, boxShadow:"0 1px 4px rgba(0,0,0,.05)" },
  ntab:       { padding:"11px 18px", fontSize:12, fontWeight:600, border:"none", background:"transparent", color:"#6b7280", cursor:"pointer", borderBottom:"3px solid transparent", fontFamily:"inherit", transition:"all .18s", display:"flex", alignItems:"center", gap:6, whiteSpace:"nowrap" },
  loadBar:    { height:3, background:"#f0f2f5" },
  loadFill:   { height:"100%", width:"40%", background:GRAD, animation:"loadAnim 1.2s infinite" },
  main:       { padding:"14px 20px", display:"flex", flexDirection:"column", gap:12 },
  secTitle:   { fontSize:19, fontWeight:700, marginTop:5 },
  secSub:     { fontSize:12, color:"#6b7280", marginTop:2 },
  ptabs:      { display:"flex", gap:4, flexWrap:"wrap" },
  ptab:       { padding:"7px 20px", borderRadius:8, border:"1.5px solid #e2e6ea", background:"#fff", color:"#6b7280", fontFamily:"inherit", fontSize:13, fontWeight:500, cursor:"pointer", transition:"all .18s", display:"flex", alignItems:"center", gap:5 },
  ptabOn:     { background:GRAD, color:"#fff", borderColor:"transparent", fontWeight:700, boxShadow:"0 3px 10px rgba(211,47,47,.25)" },
  kgrid5:     { display:"grid", gridTemplateColumns:"repeat(5,1fr)", gap:10 },
  kgrid6:     { display:"grid", gridTemplateColumns:"repeat(6,1fr)", gap:10 },
  kc:         { background:"#fff", border:"1.5px solid #e2e6ea", borderRadius:12, padding:"14px 16px", position:"relative", overflow:"hidden", transition:"all .18s" },
  kcLbl:      { fontSize:9, fontWeight:700, textTransform:"uppercase", letterSpacing:".5px", color:"#6b7280", marginBottom:5 },
  kcVal:      { fontSize:19, fontWeight:700, fontFamily:"'DM Mono',monospace", lineHeight:1.2 },
  kcSub:      { fontSize:10, marginTop:3, color:"#6b7280" },
  input:      { padding:"9px 14px", borderRadius:8, border:"1.5px solid #e2e6ea", fontFamily:"inherit", fontSize:13, outline:"none" },
  btnTgt:     { padding:"9px 22px", borderRadius:8, border:"none", background:GRAD, color:"#fff", fontFamily:"inherit", fontSize:13, fontWeight:700, cursor:"pointer" },
  card:       { background:"#fff", border:"1px solid #e2e6ea", borderRadius:12, overflow:"hidden", boxShadow:"0 1px 4px rgba(0,0,0,.04)" },
  chdr:       { padding:"11px 16px", display:"flex", justifyContent:"space-between", alignItems:"center", borderBottom:"1px solid #e2e6ea", background:"#fafbfc", flexWrap:"wrap", gap:8 },
  ctitle:     { fontSize:13, fontWeight:600 },
  btnAdd:     { padding:"7px 16px", borderRadius:7, border:"none", background:"#059669", color:"#fff", fontFamily:"inherit", fontSize:12, fontWeight:700, cursor:"pointer" },
  btnExp:     { padding:"6px 13px", borderRadius:7, border:"none", background:GRAD, color:"#fff", fontFamily:"inherit", fontSize:12, cursor:"pointer" },
  tbl:        { width:"100%", borderCollapse:"collapse", fontSize:11 },
  th:         { padding:"8px 9px", fontSize:9, fontWeight:700, textAlign:"center", border:"1px solid #e2e6ea", whiteSpace:"nowrap", letterSpacing:".3px", textTransform:"uppercase", background:"#fafbfc", color:"#6b7280", verticalAlign:"middle" },
  td:         { padding:"9px 9px", textAlign:"center", verticalAlign:"middle", fontFamily:"'DM Mono',monospace", fontSize:11 },
  tr:         { borderBottom:"1px solid #f1f5f9", transition:"background .12s" },
  tfootRow:   { background:"#f0f2f5", borderTop:"2px solid #e2e6ea", fontWeight:700 },
  insRow:     { display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:10 },
  insCard:    { background:"#fff", border:"1px solid #e2e6ea", borderRadius:12, padding:"14px 16px", display:"flex", alignItems:"flex-start", gap:12, transition:"all .18s" },
  tokPill:    { display:"inline-flex", background:"#ede9fe", border:"1px solid #c4b5fd", color:"#4c1d95", borderRadius:10, padding:"2px 8px", fontSize:11, fontWeight:700, fontFamily:"'DM Mono',monospace" },
  dehPill:    { display:"inline-flex", background:"linear-gradient(135deg,#ecfdf5,#dbeafe)", border:"1px solid #6ee7b7", color:"#065f46", borderRadius:10, padding:"2px 8px", fontSize:11, fontWeight:700, fontFamily:"'DM Mono',monospace" },
  sPending:   { display:"inline-flex", alignItems:"center", gap:5, padding:"3px 9px", borderRadius:10, fontSize:10, fontWeight:700, background:"#fef3c7", color:"#92400e", border:"1px solid #fde68a" },
  sApproved:  { display:"inline-flex", alignItems:"center", gap:5, padding:"3px 9px", borderRadius:10, fontSize:10, fontWeight:700, background:"#d1fae5", color:"#065f46", border:"1px solid #6ee7b7" },
  sRejected:  { display:"inline-flex", alignItems:"center", gap:5, padding:"3px 9px", borderRadius:10, fontSize:10, fontWeight:700, background:"#fee2e2", color:"#7f1d1d", border:"1px solid #fca5a5" },
  approveBtn: { padding:"5px 12px", border:"none", borderRadius:6, background:"#059669", color:"#fff", fontFamily:"inherit", fontSize:11, fontWeight:700, cursor:"pointer" },
  rejectBtn:  { padding:"5px 12px", border:"none", borderRadius:6, background:"#dc2626", color:"#fff", fontFamily:"inherit", fontSize:11, fontWeight:700, cursor:"pointer" },
  filterInp:  { padding:"7px 10px", borderRadius:8, border:"1.5px solid #e2e6ea", fontFamily:"inherit", fontSize:12, outline:"none", cursor:"pointer", minWidth:120 },
  filterLbl:  { fontSize:9, fontWeight:700, color:"#6b7280", textTransform:"uppercase", letterSpacing:".5px" },
  overlay:    { position:"fixed", inset:0, background:"rgba(0,0,0,.5)", zIndex:500, display:"flex", alignItems:"flex-start", justifyContent:"center", padding:20, overflowY:"auto" },
  mbox:       { background:"#fff", borderRadius:16, width:"100%", maxWidth:640, margin:"auto", boxShadow:"0 24px 64px rgba(0,0,0,.28)", maxHeight:"92vh", overflowY:"auto" },
  mhdr:       { background:GRAD, padding:"14px 18px", borderRadius:"16px 16px 0 0", display:"flex", justifyContent:"space-between", alignItems:"flex-start", position:"sticky", top:0, zIndex:10 },
  mtitle:     { color:"#fff", fontSize:14, fontWeight:700 },
  msub:       { color:"rgba(255,255,255,.75)", fontSize:11, marginTop:2 },
  mcls:       { width:28, height:28, borderRadius:"50%", border:"none", background:"rgba(255,255,255,.2)", color:"#fff", fontSize:18, cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center" },
  mbody:      { padding:16, display:"flex", flexDirection:"column", gap:11 },
  mg2:        { display:"grid", gridTemplateColumns:"1fr 1fr", gap:10 },
  mg3:        { display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:10 },
  inp:        { width:"100%", padding:"8px 10px", borderRadius:7, border:"1.5px solid #e2e6ea", fontFamily:"'DM Mono',monospace", fontSize:12, background:"#fff", color:"#1a1d23", outline:"none", transition:"border-color .15s", boxSizing:"border-box" },
  mfoot:      { padding:"11px 16px", borderTop:"1px solid #e2e6ea", display:"flex", gap:8, background:"#fafbfc", borderRadius:"0 0 16px 16px", position:"sticky", bottom:0 },
  btnCancel:  { flex:1, padding:10, borderRadius:8, border:"1.5px solid #e2e6ea", background:"#fff", fontFamily:"inherit", fontSize:13, fontWeight:600, cursor:"pointer" },
  btnSave:    { flex:2, padding:10, borderRadius:8, border:"none", background:GRAD, color:"#fff", fontFamily:"inherit", fontSize:13, fontWeight:700, cursor:"pointer" },
};
